//src/components/Modals/CreateEditAssetModal.js
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,   
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Slide,
  Container,
  Card,
  CardContent,
  CircularProgress,
  Autocomplete,
  Snackbar,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockIcon from "@mui/icons-material/Lock";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";

import { repo } from "../../dbservice/repo";
import { generateQRToken } from "../../services/qrTokenService";

import {
  sendApprovalRequestEmail,
  scheduleWarrantyReminders,
} from "../../services/brevoEmailService";

import { preferencesService } from "../../services/preferencesService";
import { assetService } from "../../services/assetService";
import { customerService } from "../../services/customerService"; 
import { singleValueListService } from "../../services/singleValueListService";
import {
  DEPRECIATION_METHODS,
  getAssetTypeConfig,
  getOwnershipTypeConfig,
  resolveEffectiveSerialPolicy,
  getSubCategoryDepreciationConfig,
} from "../../utils/assetUtils";

import SearchableDropdown from "../../pages/SearchableDropdown";
import {
  deriveApprovalStatus,
  isEditableByApproval,
} from "../../utils/approvalUtils";
// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const MAX_FILE_SIZE = 750 * 1024;
const UPLOAD_TIMEOUT_MS = 30000;

// ─────────────────────────────────────────────────────────────────────────────
// UPLOAD HELPER
// ─────────────────────────────────────────────────────────────────────────────

const uploadWithTimeout = (uploadPromise, ms = UPLOAD_TIMEOUT_MS) =>
  Promise.race([
    uploadPromise,
    new Promise((_, reject) =>
      setTimeout(
        () =>
          reject(
            new Error(
              "Upload timed out after 30s. Check your network connection or Firebase Storage CORS configuration.",
            ),
          ),
        ms,
      ),
    ),
  ]);

// ─────────────────────────────────────────────────────────────────────────────
// DEPRECIATION HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const parseOptionalNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
};

const calculateYearsElapsed = (purchaseDateStr) => {
  if (!purchaseDateStr) return 0;
  const purchaseDate = new Date(`${purchaseDateStr}T00:00:00`);
  const today = new Date();
  if (Number.isNaN(purchaseDate.getTime())) return 0;
  if (purchaseDate > today) return 0;
  const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;
  return Math.max(0, (today.getTime() - purchaseDate.getTime()) / MS_PER_YEAR);
};

const calculateCurrentBookValueWDV = (openingValue, rate, purchaseDateStr) => {
  const bookValue = toNumber(openingValue);
  const depRate = toNumber(rate);
  if (bookValue <= 0 || depRate <= 0) return bookValue;
  const yearsElapsed = calculateYearsElapsed(purchaseDateStr);
  if (yearsElapsed <= 0) return bookValue;
  return Math.max(bookValue * Math.pow(1 - depRate / 100, yearsElapsed), 0);
};

const calculateSLMDepreciation = (cost, residual, life) => {
  const assetCost = toNumber(cost);
  const residualValue = toNumber(residual);
  const usefulLife = toNumber(life);
  if (assetCost <= 0 || usefulLife <= 0) return 0;
  return Math.max(assetCost - residualValue, 0) / usefulLife;
};

const calculateWDVDepreciation = (openingValue, rate, residual = 0) => {
  const bookValue = toNumber(openingValue);
  const depreciationRate = toNumber(rate);
  const residualValue = toNumber(residual);
  if (bookValue <= 0 || depreciationRate <= 0 || bookValue <= residualValue)
    return 0;
  const depreciation = bookValue * (depreciationRate / 100);
  return Math.min(depreciation, Math.max(bookValue - residualValue, 0));
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(toNumber(amount));

const formatFormDate = (dateField) => {
  if (!dateField) return "";
  let dateObj;
  if (typeof dateField.toDate === "function") {
    dateObj = dateField.toDate();
  } else if (
    dateField &&
    typeof dateField === "object" &&
    (dateField.seconds !== undefined || dateField._seconds !== undefined)
  ) {
    const seconds =
      dateField.seconds !== undefined ? dateField.seconds : dateField._seconds;
    dateObj = new Date(seconds * 1000);
  } else {
    dateObj = new Date(dateField);
  }
  if (dateObj instanceof Date && !Number.isNaN(dateObj.getTime())) {
    return dateObj.toISOString().split("T")[0];
  }
  return "";
};

const generateUUID = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPUTE DEPRECIATION PREVIEW
// ─────────────────────────────────────────────────────────────────────────────

const computeDepreciationPreview = ({
  purchaseValue,
  residualValue,
  depreciationMethod,
  usefulLife,
  depreciationRate,
  openingBookValue,
  purchaseDate,
  subCategoryUsefulLife,
  subCategoryDepRate,
}) => {
  const cost = toNumber(purchaseValue);
  const residual = toNumber(residualValue, 0);
  const method = depreciationMethod;
  const purchaseDateStr = purchaseDate;

  if (!method) return null;
  if (cost <= 0 || residual < 0 || residual > cost) return null;

  let effectiveUsefulLife = toNumber(usefulLife);
  let effectiveRate = toNumber(depreciationRate);

  if (
    method === DEPRECIATION_METHODS.SLM &&
    effectiveUsefulLife <= 0 &&
    subCategoryUsefulLife
  )
    effectiveUsefulLife = toNumber(subCategoryUsefulLife);
  if (
    method === DEPRECIATION_METHODS.WDV &&
    effectiveRate <= 0 &&
    subCategoryDepRate
  )
    effectiveRate = toNumber(subCategoryDepRate);

  const yearsElapsed = calculateYearsElapsed(purchaseDateStr);

  if (method === DEPRECIATION_METHODS.SLM) {
    const life = effectiveUsefulLife;
    if (life <= 0) return null;

    const annual = calculateSLMDepreciation(cost, residual, life);
    const monthly = annual / 12;
    const daily = annual / 365;
    const depreciableAmount = Math.max(cost - residual, 0);
    const totalDepToDate = Math.min(annual * yearsElapsed, depreciableAmount);
    const currentBookValue = Math.max(cost - totalDepToDate, residual);

    const yearValues = Array.from({ length: Math.min(life, 50) }, (_, i) => {
      const year = i + 1;
      const opening = Math.max(cost - annual * (year - 1), residual);
      const closing = Math.max(cost - annual * year, residual);
      const accumulated = Math.min(annual * year, depreciableAmount);
      return {
        year,
        opening: Number(opening.toFixed(2)),
        depreciation: Number(
          Math.min(annual, Math.max(opening - residual, 0)).toFixed(2),
        ),
        closing: Number(closing.toFixed(2)),
        accumulated: Number(accumulated.toFixed(2)),
      };
    });

    return {
      method: "SLM",
      label: "Straight-Line Method",
      annualDepreciation: Number(annual.toFixed(2)),
      monthlyDepreciation: Number(monthly.toFixed(2)),
      dailyDepreciation: Number(daily.toFixed(2)),
      depreciableAmount: Number(depreciableAmount.toFixed(2)),
      years: life,
      yearsElapsed: Number(yearsElapsed.toFixed(2)),
      formula: `(${formatCurrency(cost)} − ${formatCurrency(residual)}) ÷ ${life}`,
      yearValues,
      currentBookValue: Number(currentBookValue.toFixed(2)),
      accumulatedDepreciation: Number(totalDepToDate.toFixed(2)),
      residualUsed: residual,
    };
  }

  if (method === DEPRECIATION_METHODS.WDV) {
    const rate = effectiveRate;
    if (rate <= 0) return null;

    const obvSource = toNumber(openingBookValue || purchaseValue);
    if (obvSource <= 0) return null;

    const currentBookValue = calculateCurrentBookValueWDV(
      obvSource,
      rate,
      purchaseDateStr,
    );
    const accumulatedDepreciation = obvSource - currentBookValue;

    const yearValues = [];
    let openingVal = obvSource;
    let scheduleAccum = 0;

    for (let year = 1; year <= 5; year += 1) {
      const depreciation = Number(
        calculateWDVDepreciation(openingVal, rate, 0).toFixed(2),
      );
      const closingValue = Number(
        Math.max(openingVal - depreciation, 0).toFixed(2),
      );
      scheduleAccum = Number((scheduleAccum + depreciation).toFixed(2));
      yearValues.push({
        year,
        opening: Number(openingVal.toFixed(2)),
        depreciation,
        closing: closingValue,
        accumulated: scheduleAccum,
      });
      openingVal = closingValue;
    }

    const annual = yearValues[0]?.depreciation || 0;

    return {
      method: "WDV",
      label: "Written Down Value",
      annualDepreciation: annual,
      monthlyDepreciation: Number((annual / 12).toFixed(2)),
      dailyDepreciation: Number((annual / 365).toFixed(2)),
      rate,
      openingBookValue: Number(obvSource.toFixed(2)),
      yearsElapsed: Number(yearsElapsed.toFixed(2)),
      formula: `${formatCurrency(obvSource)} × ${rate}%`,
      depreciableAmount: Number(
        (obvSource - yearValues[yearValues.length - 1].closing).toFixed(2),
      ),
      yearValues,
      currentBookValue: Number(currentBookValue.toFixed(2)),
      accumulatedDepreciation: Number(accumulatedDepreciation.toFixed(2)),
      residualUsed: 0,
    };
  }

  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// SLIDE TRANSITION
// ─────────────────────────────────────────────────────────────────────────────

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─────────────────────────────────────────────────────────────────────────────
// STYLED COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const FormGroup = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hasError",
})(({ hasError }) => ({
  flex: 1,
  minWidth: "200px",
  display: "flex",
  flexDirection: "column",
  gap: "0.375rem",
  position: "relative",
  overflow: "visible",
  "& .MuiOutlinedInput-root": {
    background: "#fff",
    "& fieldset": { borderColor: hasError ? "#dc2626" : "#d1d5db" },
    "&:hover fieldset": { borderColor: hasError ? "#dc2626" : "#3b82f6" },
    "&.Mui-focused fieldset": {
      borderColor: hasError ? "#dc2626" : "#3b82f6",
      boxShadow: hasError
        ? "0 0 0 3px rgba(220,38,38,0.12)"
        : "0 0 0 3px rgba(59,130,246,0.12)",
    },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    backgroundColor: "#f3f4f6",
    color: "#6b7280",
    cursor: "not-allowed",
  },
}));

const FormRow = styled(Box)(() => ({
  display: "flex",
  gap: "1rem",
  marginBottom: "1rem",
  flexWrap: "wrap",
  "&:last-child": { marginBottom: 0 },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: "0.8125rem",
  fontWeight: 600,
  color: "#374151",
  textTransform: "uppercase",
  letterSpacing: "0.025em",
}));

const RequiredMark = styled("span")(() => ({
  color: "#ef4444",
  marginLeft: "0.125rem",
}));

const ErrorMessage = styled(FormHelperText)(() => ({
  color: "#dc2626",
  fontSize: "0.75rem",
  fontWeight: 500,
  margin: 0,
}));

const FieldHint = styled(Typography)(() => ({
  fontSize: "0.75rem",
  color: "#9ca3af",
  fontStyle: "italic",
}));

const InfoBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "warning" && prop !== "success",
})(({ warning, success }) => ({
  padding: "0.75rem 1rem",
  background: warning ? "#fffbeb" : success ? "#f0fdf4" : "#eff6ff",
  border: `1px solid ${warning ? "#fde68a" : success ? "#bbf7d0" : "#bfdbfe"}`,
  borderRadius: "6px",
  marginBottom: "1rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: "0.5rem",
  fontSize: "0.8125rem",
  color: warning ? "#92400e" : success ? "#166534" : "#1e40af",
}));

const LinkButton = styled(Button)(() => ({
  background: "none",
  border: "1px solid currentColor",
  borderRadius: "4px",
  padding: "0.25rem 0.75rem",
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "inherit",
  whiteSpace: "nowrap",
  textTransform: "none",
  "&:hover": { background: "rgba(0,0,0,0.05)" },
  "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
}));

const PreviewBox = styled(Box)(() => ({
  padding: "1.25rem",
  background: "#f0fdf4",
  border: "1px solid #bbf7d0",
  borderRadius: "8px",
  marginTop: "0.5rem",
}));

const PreviewGrid = styled(Box)(() => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
  gap: "1rem",
  marginBottom: "1.25rem",
}));

const ScheduleGrid = styled(Box)(() => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
  gap: "0.75rem",
}));

const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: "1px solid #e2e8f0",
}));

const SectionTitle = styled(Typography)(() => ({
  fontSize: "0.9375rem",
  fontWeight: 700,
  color: "#1e293b",
  marginBottom: "1.25rem",
  paddingBottom: "0.75rem",
  borderBottom: "1px solid #e5e7eb",
}));

// ─────────────────────────────────────────────────────────────────────────────
// SX CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const helperErrorSx = {
  color: "#dc2626",
  fontSize: "0.75rem",
  fontWeight: 500,
  margin: 0,
  marginTop: "4px",
};

const calcRowSx = {
  background: "#f8fafc",
  padding: "14px",
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  marginTop: "8px",
};

const annualFieldSx = {
  "& .MuiOutlinedInput-root": {
    background: "#ecfdf5",
    fontWeight: 700,
    color: "#065f46",
    fontSize: "16px",
  },
};

const readOnlyFieldSx = {
  "& .MuiOutlinedInput-root": { background: "#f9fafb", fontWeight: 600 },
};

const inheritedFieldSx = {
  "& .MuiOutlinedInput-root": { background: "#f3f4f6" },
};

const residualFieldSx = {
  "& .MuiInputBase-input.Mui-disabled": {
    backgroundColor: "#f3f4f6",
    color: "#6b7280",
    cursor: "not-allowed",
    opacity: 0.7,
  },
};

const cancelBtnSx = {
  px: 3,
  py: 1,
  backgroundColor: "#f3f4f6",
  color: "#374151",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontWeight: 500,
  textTransform: "none",
  minWidth: "100px",
  height: "40px",
  "&:hover": { backgroundColor: "#e5e7eb" },
  "&:disabled": { opacity: 0.6 },
};

const submitBtnSx = {
  px: 3,
  py: 1,
  backgroundColor: "#2563eb",
  color: "#fff",
  borderRadius: "6px",
  fontWeight: 600,
  textTransform: "none",
  minWidth: "160px",
  height: "40px",
  display: "flex",
  alignItems: "center",
  gap: 1,
  "&:hover": { backgroundColor: "#1d4ed8" },
  "&:disabled": { backgroundColor: "#93c5fd" },
};

const InheritedNote = ({ parentName }) => (
  <FieldHint sx={{ color: "#1e40af", fontStyle: "normal", mt: 0.25 }}>
    ↑ Inherited from <strong>{parentName || "Primary Asset"}</strong>
  </FieldHint>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const CreateEditAssetModal = ({
  open,
  asset = null,
  onClose,
  onSave,
  onError,
  onNavigateToMoveAssets,
  onNavigateToAssignAsset,
  currentUser,
  categories = [],
  subCategories = [],
  brands = [],
  vendors = [],
  departments = [],
  users = [],
  locations = [],
}) => {
  const isEditMode = useMemo(() => Boolean(asset), [asset]);

  const isAssignmentLocked = useMemo(
    () =>
      isEditMode && Boolean(asset?.assignedTo || asset?.assignedDepartmentId),
    [isEditMode, asset],
  );

const isLocationLocked = useMemo(
  () => {
    if (!isEditMode) return false;
    return Boolean(asset?.locationCode && asset.locationCode.trim() !== "");
  },
  [isEditMode, asset],
);

  
  // ── Single Value List state (replaces hardcoded dropdowns) ──
    const [svlLoading, setSvlLoading] = useState(false);
  const [customers, setCustomers] = useState([]); 
  const [svlLists, setSvlLists] = useState({
    asset_types: [],
    ownership_types: [],
    depreciation_methods: [],
    maintenance_intervals: [],
    asset_statuses: [],
    billing_frequencies: [],
    units_of_measurement: [],
  });
 const [ownershipEnabled, setOwnershipEnabled] = useState(false);
  const [ownershipPrefLoading, setOwnershipPrefLoading] = useState(false);
    // ── Approval Workflow state  ──
  const [approverEnabled, setApproverEnabled] = useState(false);
  const [approverPrefLoading, setApproverPrefLoading] = useState(false);
  const [svlApprovers, setSvlApprovers] = useState([]);
    useEffect(() => {
    if (!open || !currentUser?.companyId) {
      setOwnershipEnabled(false);
      return;
    }
    let cancelled = false;
    setOwnershipPrefLoading(true);
    preferencesService
      .isOwnershipEnabled(currentUser)
      .then((enabled) => {
        if (!cancelled) setOwnershipEnabled(Boolean(enabled));
      })
      .catch((err) => {
        console.warn("Ownership preference load failed:", err);
        if (!cancelled) setOwnershipEnabled(false);
      })
      .finally(() => {
        if (!cancelled) setOwnershipPrefLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, currentUser]);
    // ── Approval Workflow: preference toggle + SVL approver roster ──
  useEffect(() => {
    if (!open || !currentUser?.companyId) {
      setApproverEnabled(false);
      setSvlApprovers([]);
      return;
    }
    let cancelled = false;
    setApproverPrefLoading(true);

    Promise.all([
      preferencesService.isApproverEnabled(currentUser),
      singleValueListService.getApproverItems(currentUser.companyId),
    ])
      .then(([enabled, items]) => {
        if (cancelled) return;
        setApproverEnabled(Boolean(enabled));
        setSvlApprovers(items || []);
      })
      .catch((err) => {
        console.warn("Approver preference / roster load failed:", err);
        if (!cancelled) {
          setApproverEnabled(false);
          setSvlApprovers([]);
        }
      })
      .finally(() => {
        if (!cancelled) setApproverPrefLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, currentUser]);
  useEffect(() => {
    if (!open || !currentUser) return;
    let cancelled = false;
    setSvlLoading(true);

    const wanted = [
      "asset_types",
      "ownership_types",
      "depreciation_methods",
      "maintenance_intervals",
      "asset_statuses",
      "billing_frequencies",
      "units_of_measurement",
    ];

    Promise.all(
      wanted.map((name) =>
        singleValueListService
          .getItems(name, { companyId: currentUser.companyId })
          .then((items) => [name, items || []])
          .catch(() => [name, []]),
      ),
    )
      .then((pairs) => {
        if (cancelled) return;
        setSvlLists(Object.fromEntries(pairs));
      })
           .finally(() => {
        if (!cancelled) setSvlLoading(false);
      });

       return () => {
      cancelled = true;
    };
  }, [open, currentUser]);

  // Fetch Customers for Customer Asset
  useEffect(() => {
    if (!open || !currentUser) return;
    let cancelled = false;
    customerService.fetchCustomers({ companyId: currentUser.companyId, status: 'active' })
      .then((data) => {
        if (!cancelled) setCustomers(data || []);
      })
      .catch((err) => console.error("Failed to load customers", err));
    return () => { cancelled = true; };
  }, [open, currentUser]);


  // Convenience aliases (SVL items are { label, value })
  const assetTypeOptions = svlLists.asset_types;
  const ownershipTypeOptions = svlLists.ownership_types;
  const billingFrequencyOptions = svlLists.billing_frequencies;
  const depreciationMethodOptions = svlLists.depreciation_methods;
  const maintenanceIntervalOptions = svlLists.maintenance_intervals;
  const assetStatusOptions = svlLists.asset_statuses;
  const unitOfMeasurementOptions = svlLists.units_of_measurement;

  // Combined loading flag for all SVL-backed dropdowns
  const isLoadingOptions = svlLoading;
 
     
  const approverOptionsByLevel = useMemo(() => {
    const map = new Map();

    if (svlApprovers.length > 0) {
      for (const row of svlApprovers) {
        const level = Number(row.level) || 1;
        const label = String(row.label || `Level ${level}`).trim();
        const usersList = Array.isArray(row.users) ? row.users : [];

        const normalizedUsers = usersList
          .map((e) => String(e || "").trim().toLowerCase())
          .filter((e) => e.includes("@"))
          .map((email) => {
            const found = (users || []).find(
              (u) => String(u.email || "").toLowerCase() === email,
            );
            const displayName = found?.name || found?.displayName || email;
            return { label: displayName, value: email };
          });

        if (normalizedUsers.length === 0) continue;

        if (map.has(level)) {
          const existing = map.get(level);
          const merged = [
            ...existing.users,
            ...normalizedUsers.filter(
              (u) => !existing.users.some((x) => x.value === u.value),
            ),
          ];
          map.set(level, { label: existing.label, users: merged });
        } else {
          map.set(level, { label, users: normalizedUsers });
        }
      }
    }

    return map;
  }, [svlApprovers, users]);

  // Sorted list of levels available 
 const approverLevels = useMemo(() => {
    const fromAsset = Array.isArray(asset?.approverList)
      ? asset.approverList
          .map((r) => Number(r.level))
          .filter((n) => Number.isFinite(n) && n > 0)
      : [];

    if (isEditMode) {
      return Array.from(new Set(fromAsset)).sort((a, b) => a - b);
    }

    return Array.from(approverOptionsByLevel.keys()).sort((a, b) => a - b);
  }, [approverOptionsByLevel, asset, isEditMode]);

  const isApproverLevelLocked = useCallback(
    (lvl) => {
      if (!isEditMode) return false;

      const list = Array.isArray(asset?.approverList) ? asset.approverList : [];
      const row = list.find((r) => Number(r.level) === lvl);
      const status = String(row?.status || "").toLowerCase();

      const isTerminalRework =
        status.includes("return") ||
        status.includes("rejected") ||
        status.includes("correction");

      if (isTerminalRework) return false;

      // A level is locked only when it has fully approved.
      if (status.startsWith("approved")) return true;

      // Single-level workflow: lock once submitted (any non-pending status).
      if (approverLevels.length === 1) {
        return !status.startsWith("pending");
      }

     
      return false;
    },
    [isEditMode, asset, approverLevels.length],
  );

  // Flat list of all users 
  const approverOptions = useMemo(
    () =>
      approverLevels.flatMap((lvl) => {
        const entry = approverOptionsByLevel.get(lvl);
        return (entry?.users || []).map((u) => ({
          ...u,
          level: lvl,
          levelLabel: entry.label,
        }));
      }),
    [approverLevels, approverOptionsByLevel],
  );

  const approverSourceIsSVL = svlApprovers.length > 0;
const buildInitialState = useCallback((a, u) => {
    const savedSerializationType = a?.serializationType || "";
    const initialTrackingMode =
      savedSerializationType === "non-serialized" ? "quantity" : "serialized";

    return {
      assetId: a?.assetId || "",
      assetName: a?.assetName || "",
      assetDescription: a?.assetDescription || "",
      company: a?.company || u?.company || "",
      companyId: a?.companyId || u?.companyId || "",
            purchaseValue: a?.purchaseValue?.toString() || "",
      salesDate: a?.salesDate ? formatFormDate(a.salesDate) : "",
      salesValue: a?.salesValue?.toString() || "",
      customerId: a?.customerId || "",
      customerName: a?.customerName || "",
            salesInvoiceNumber: a?.salesInvoiceNumber || "",
      salesInvoiceFileUrl: a?.salesInvoiceFileUrl || "",
      salesInvoiceFile: null,
      salesInvoiceFileName: a?.salesInvoiceFileName || "",
      locationCode: a?.locationCode || "",
      currentLocationName: a?.currentLocationName || "",
      status: a?.status || "Available",
      vendorId: a?.vendorId || "",
      vendorName: a?.vendorName || "",
            assignedTo: a?.assignedTo || "",
      assignedToName: a?.assignedToName || "",
      assignedDepartmentId: a?.assignedDepartmentId || "",
      assignedDepartmentName: a?.assignedDepartmentName || "",
      ownerId: a?.ownerId || "",
      ownerName: a?.ownerName || "",
      approverList: Array.isArray(a?.approverList) ? a.approverList : [],
      approvalHistory: Array.isArray(a?.approvalHistory) ? a.approvalHistory : [],
     approvalStatus: a?.approvalStatus || "Draft",
      categoryId: a?.categoryId || "",
      categoryName: a?.categoryName || "",
      categoryCode: a?.categoryCode || "",
      subCategoryId: a?.subCategoryId || "",
      subCategoryName: a?.subCategoryName || "",
      brandId: a?.brandId || "",
      brandName: a?.brandName || "",
      invoiceNumber: a?.invoiceNumber || "",
      purchaseDate: a?.purchaseDate ? formatFormDate(a.purchaseDate) : "",
      warrantyExpiry: a?.warrantyExpiry ? formatFormDate(a.warrantyExpiry) : "",
      depreciationSchedule: Array.isArray(a?.depreciationSchedule)
        ? a.depreciationSchedule
        : [],
      maintenanceFrequency: a?.maintenanceFrequency || "",
      maintenanceStartDate: a?.maintenanceStartDate
        ? formatFormDate(a.maintenanceStartDate)
        : "",
      manufacturerSerialNumber: a?.manufacturerSerialNumber || "",
      quantity: a?.quantity?.toString() || "",
      unitOfMeasurement: a?.unitOfMeasurement || "",
      reorderLevel: a?.reorderLevel?.toString() || "",
      expiryDate: a?.expiryDate ? formatFormDate(a.expiryDate) : "",
      invoiceFile: null,
      invoiceFileUrl: a?.invoiceFileUrl || "",
      invoiceFileName: a?.invoiceFileName || "",
            depreciationMethod: (() => {
        // Get the raw value from DB
        const raw = String(a?.depreciationMethod || a?.depreciation || "").trim();
        const upper = raw.toUpperCase();
        
        // Map legacy or lowercase values to match SVL exactly
        if (upper === "SLM" || upper === "STRAIGHT-LINE" || upper === "STRAIGHT LINE") return "SLM";
        if (upper === "WDV" || upper === "WRITTEN-DOWN-VALUE" || upper === "WRITTEN DOWN VALUE") return "WDV";
        if (upper === "NONE" || upper === "NOT DEPRECIABLE") return "None";
        
        return raw; // Return as-is if it's something else
      })(),
      usefulLife: a?.usefulLife?.toString() || "",
      depreciationRate: a?.depreciationRate?.toString() || "",
      residualValue: a?.residualValue?.toString() || "",
      isDepreciationAuto:
        a?.isDepreciationAuto !== undefined ? a.isDepreciationAuto : true,
      openingBookValue:
        a?.openingBookValue?.toString() || a?.purchaseValue?.toString() || "",
      isOpeningBookValueManual:
        a?.isOpeningBookValueManual !== undefined
          ? a.isOpeningBookValueManual
          : false,
      annualDepreciation: "",
      monthlyDepreciation: "",
      dailyDepreciation: "",
      currentBookValue: "",
      accumulatedDepreciation: "",

    
      assetType: a?.assetType || "Primary",
      ownershipType: a?.ownershipType || "Owned",

      parentAssetId: a?.parentAssetId || "",
      parentAssetName: a?.parentAssetName || "",
      purchasedSeparately: a?.purchasedSeparately ?? false,

      leaseStartDate: a?.leaseStartDate ? formatFormDate(a.leaseStartDate) : "",
      leaseEndDate: a?.leaseEndDate ? formatFormDate(a.leaseEndDate) : "",
      leaseAmount: a?.leaseAmount?.toString() || "",
      leaseVendorId: a?.leaseVendorId || "",
      leaseVendorName: a?.leaseVendorName || "",
      leaseAgreementNumber: a?.leaseAgreementNumber || "",
      leaseBillingFrequency: a?.leaseBillingFrequency || "",

      rentStartDate: a?.rentStartDate ? formatFormDate(a.rentStartDate) : "",
      expectedReturnDate: a?.expectedReturnDate
        ? formatFormDate(a.expectedReturnDate)
        : "",
      rentAmount: a?.rentAmount?.toString() || "",
      rentVendorId: a?.rentVendorId || "",
      rentVendorName: a?.rentVendorName || "",
      rentAgreementNumber: a?.rentAgreementNumber || "",
      rentBillingFrequency: a?.rentBillingFrequency || "",

      userTrackingMode: initialTrackingMode,
    };
  }, []);
 
  useEffect(() => {
    if (open && !initializedRef.current) {
      setFormData(buildInitialState(asset, currentUser));
      setErrors({});
      setSubmitError("");
      setSubCategoryData(null);
      setInheritedFromParent(null);
      setAssetIdStatus("idle");
      initializedRef.current = true;
    }
       if (!open) {
      initializedRef.current = false;
    }
  }, [open, asset, currentUser, buildInitialState]);

   useEffect(() => {
    if (!open || !approverEnabled) return;
    if (approverLevels.length === 0) return;

    
    if (isEditMode) {
      const originalList = Array.isArray(asset?.approverList)
        ? asset.approverList
        : [];
      if (originalList.length === 0) return;
    }

    setFormData((prev) => {
      const existingList = Array.isArray(prev.approverList)
        ? prev.approverList
        : [];
      const existingByLevel = new Map(
        existingList.map((row) => [Number(row.level), row]),
      );

      const nextList = approverLevels.map((lvl) => {
        const entry = approverOptionsByLevel.get(lvl);
        const prior = existingByLevel.get(lvl);

        const rosterEmails = Array.from(
          new Set(
            (entry?.users || [])
              .map((u) => String(u.value || "").trim().toLowerCase())
              .filter((e) => e.includes("@")),
          ),
        );

      
        const defaultEmail = rosterEmails.length === 1 ? rosterEmails[0] : "";

        const finalSelectedUsers =
          Array.isArray(prior?.users) && prior.users.length > 0
            ? prior.users
            : (defaultEmail ? [defaultEmail] : []);

        // Preserve any already-approved state for this level.
        const priorApproved = Array.isArray(prior?.approvedUsers)
          ? prior.approvedUsers
              .map((e) => String(e).toLowerCase())
              .filter((e) => finalSelectedUsers.some(
                (u) => String(u).toLowerCase() === e,
              ))
          : [];

        return {
          level: lvl,
          label: entry?.label || prior?.label || `Level ${lvl}`,
          users: finalSelectedUsers,
          approvedUsers: priorApproved,
          currentIndex: prior?.currentIndex ?? 0,
          actedBy: prior?.actedBy || null,
          actedAt: prior?.actedAt || null,
          status: prior?.status || `Pending Level ${lvl}`,
          email: finalSelectedUsers[0] || null,
          name: prior?.name || null,
        };
      });

      
      const same =
        existingList.length === nextList.length &&
        existingList.every((row, i) => {
          const n = nextList[i];
          return (
            Number(row.level) === n.level &&
            JSON.stringify(row.users || []) === JSON.stringify(n.users || []) &&
            JSON.stringify(row.approvedUsers || []) ===
              JSON.stringify(n.approvedUsers || []) &&
            (row.currentIndex ?? 0) === (n.currentIndex ?? 0) &&
            (row.actedBy || null) === (n.actedBy || null) &&
            row.status === n.status
          );
        });
      if (same) return prev;

      return { ...prev, approverList: nextList };
    });
  }, [open, approverEnabled, approverLevels, approverOptionsByLevel]);

  const requiresParentAsset = useCallback(
    (t) => getAssetTypeConfig(t).requiresParent,
    [],
  );
  const canBeParent = useCallback((t) => getAssetTypeConfig(t).canBeParent, []);
  const hasProcurementInheritance = useCallback(
    (t) => getAssetTypeConfig(t).hasProcurementInheritance,
    [],
  );
  const hasLocationInheritance = useCallback(
    (t) => getAssetTypeConfig(t).hasLocationInheritance,
    [],
  );

  const showParentAssetField = useCallback(
    (t) => {
      if (!t) return false;
      if (canBeParent(t)) return false;
      return true;
    },
    [canBeParent],
  );


  const [assetIdStatus, setAssetIdStatus] = useState("idle");
  const [formData, setFormData] = useState(() =>
    buildInitialState(asset, currentUser),
  );
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  
  const [subCategoryData, setSubCategoryData] = useState(null);

  const [parentAssets, setParentAssets] = useState([]);
  const [parentAssetsLoading, setParentAssetsLoading] = useState(false);
  const [inheritedFromParent, setInheritedFromParent] = useState(null);
  const initializedRef = useRef(false);
  const formDataRef = useRef(formData);
  const fileInputRef = useRef(null);
  const salesFileInputRef = useRef(null);
  const formBodyRef = useRef(null);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const showToast = useCallback(
    (message, type = "error") => {
      if (onError) onError(message, type);
    },
    [onError],
  );

  // ── Guard: block edit submissions when asset is not in a rework-able state.
  const assetApprovalLabel = useMemo(() => {
    if (!asset) return null;
    if (Array.isArray(asset.approverList) && asset.approverList.length > 0) {
      return deriveApprovalStatus(asset.approverList);
    }
    return "NA";
  }, [asset]);

  const isSubmissionBlockedByApproval = useMemo(() => {
    if (!isEditMode) return false;
    return !isEditableByApproval(asset);
  }, [isEditMode, asset]);

  const currentAssetTypeCfg = useMemo(
    () => getAssetTypeConfig(formData.assetType),
    [formData.assetType],
  );

  const currentOwnershipTypeCfg = useMemo(
    () => getOwnershipTypeConfig(formData.ownershipType),
    [formData.ownershipType],
  );
// Is ownership Rented or Leased?
const isReturnOnlyOwnership =
  formData.ownershipType === "Rented" ||
  formData.ownershipType === "Leased";

// Categories that have at least one serialized sub-category
const selectableCategories = useMemo(() => {
  // Owned → show everything, same as before
  if (!isReturnOnlyOwnership) return categories;

  // Rented/Leased → keeps only categories that have a serialized sub-category
  const serializedCategoryIds = new Set();
  for (const sc of subCategories) {
    const method =
      sc.assetTrackingMethod || sc.trackingMethod || sc.serializationType || "";
    if (method === "serialized" && sc.categoryId) {
      serializedCategoryIds.add(sc.categoryId);
    }
  }
  return categories.filter((c) => serializedCategoryIds.has(c.id));
}, [categories, subCategories, isReturnOnlyOwnership]);

// Sub-categories for the chosen category.
// Rented/Leased → only serialized ones.
const filteredSubCategories = useMemo(() => {
  if (!formData.categoryId) return [];
  let list = subCategories.filter((sc) => sc.categoryId === formData.categoryId);
  if (isReturnOnlyOwnership) {
    list = list.filter((sc) => {
      const method =
        sc.assetTrackingMethod || sc.trackingMethod || sc.serializationType || "";
      return method === "serialized";
    });
  }
  return list;
}, [subCategories, formData.categoryId, isReturnOnlyOwnership]);
  
useEffect(() => {
  if (!isReturnOnlyOwnership) return;
  if (!formData.categoryId) return;

  // Does the currently-selected category exist in the filtered list?
  const stillValid = selectableCategories.some(
    (c) => c.id === formData.categoryId
  );
  if (stillValid) return;

  // If not, clear category + sub-category so user must re-pick
  setFormData((prev) => ({
    ...prev,
    categoryId: "",
    categoryName: "",
    categoryCode: "",
    subCategoryId: "",
    subCategoryName: "",
    brandId: "",
    brandName: "",
  }));
  setSubCategoryData(null);
}, [isReturnOnlyOwnership, formData.categoryId, selectableCategories]);

 const currentSerialPolicy = useMemo(() => {
  // Rented / Leased assets are ALWAYS serialized
  if (isReturnOnlyOwnership) return "required";
  return resolveEffectiveSerialPolicy(formData.assetType, subCategoryData);
}, [formData.assetType, subCategoryData, isReturnOnlyOwnership]);

  const currentTracksQuantity = currentAssetTypeCfg.tracksQuantity;
  const currentHasReorderLevel = currentAssetTypeCfg.hasReorderLevel;
  const currentHasExpiryDate = currentAssetTypeCfg.hasExpiryDate;

  const showSerialField = currentSerialPolicy !== "disabled";
  const serialIsRequired = currentSerialPolicy === "required";
  const allowSerialToggle = currentSerialPolicy === "optional";

  const isSerializedAsset =
    currentSerialPolicy === "required"
      ? true
      : currentSerialPolicy === "disabled"
        ? false
        : formData.userTrackingMode === "serialized";

  const showQuantityFields =
    currentTracksQuantity || (allowSerialToggle && !isSerializedAsset);

  const trackingModeLabel = useMemo(() => {
    if (!formData.assetType) return "";
    const assetTypePolicy = getAssetTypeConfig(formData.assetType).serialPolicy;

    if (assetTypePolicy === "disabled") return "Non-Serialized (Bulk Quantity)";
    if (assetTypePolicy === "required") return "Serialized (Individual)";

    if (!formData.subCategoryId) return "";

    if (currentSerialPolicy === "disabled")
      return "Non-Serialized (Bulk Quantity)";
    if (currentSerialPolicy === "required") return "Serialized (Individual)";
    return isSerializedAsset ? "Serialized (Individual)" : "Bulk (Quantity)";
  }, [
    formData.assetType,
    formData.subCategoryId,
    currentSerialPolicy,
    isSerializedAsset,
  ]);

  const trackingModeSource = useMemo(() => {
    if (!formData.assetType) return "";
    const assetTypePolicy = getAssetTypeConfig(formData.assetType).serialPolicy;
    if (assetTypePolicy === "disabled")
      return "Enforced by Asset Type — always Bulk Quantity";
    if (assetTypePolicy === "required")
      return "Enforced by Asset Type — always Serialized";
    if (!formData.subCategoryId)
      return "Select a Sub-Category to apply its tracking setting";

    const subCatMethod =
      subCategoryData?.assetTrackingMethod ||
      subCategoryData?.trackingMethod ||
      subCategoryData?.serializationType ||
      "";

    if (subCatMethod === "serialized")
      return `Set by Sub-Category "${subCategoryData?.subCategoryName}" — Serialized`;
    if (subCatMethod === "non-serialized")
      return `Set by Sub-Category "${subCategoryData?.subCategoryName}" — Non-Serialized`;
    return "Sub-Category has no tracking preference — you may choose below";
  }, [formData.assetType, formData.subCategoryId, subCategoryData]);

  const showsFinancialSection = currentOwnershipTypeCfg.showsFinancialSection;
  const showsLeaseFields = currentOwnershipTypeCfg.showsLeaseFields;
  const showsRentalFields = currentOwnershipTypeCfg.showsRentalFields;
  const requiresBillingFrequency =
    currentOwnershipTypeCfg.requiresBillingFrequency;
  const showsDepreciation = currentOwnershipTypeCfg.showsDepreciation;

  const showPurchasedSeparatelyUI =
    hasProcurementInheritance(formData.assetType) &&
    showsFinancialSection &&
    Boolean(formData.parentAssetId);

  const isProcurementInherited =
    hasProcurementInheritance(formData.assetType) &&
    showsFinancialSection &&
    !formData.purchasedSeparately &&
    Boolean(inheritedFromParent);

  const depreciationPreview = useMemo(
    () =>
      computeDepreciationPreview({
        purchaseValue: formData.purchaseValue,
        residualValue: formData.residualValue,
        depreciationMethod: formData.depreciationMethod,
        usefulLife: formData.usefulLife,
        depreciationRate: formData.depreciationRate,
        openingBookValue: formData.openingBookValue,
        purchaseDate: formData.purchaseDate,
        subCategoryUsefulLife: subCategoryData?.usefulLife ?? null,
        subCategoryDepRate: subCategoryData?.depreciationRate ?? null,
      }),
    [
      formData.purchaseValue,
      formData.residualValue,
      formData.depreciationMethod,
      formData.usefulLife,
      formData.depreciationRate,
      formData.openingBookValue,
      formData.purchaseDate,
      subCategoryData,
    ],
  );

  const isResidualValueReadOnly =
    formData.depreciationMethod === DEPRECIATION_METHODS.WDV;

  
   useEffect(() => {
    if (!open || !currentUser || !showParentAssetField(formData.assetType)) {
      setParentAssets([]);
      return;
    }
    
    let cancelled = false;
    setParentAssetsLoading(true);
    
    const targetCompanyId = currentUser.companyId || currentUser.company || "";
    
    assetService
      .fetchAssets({ companyId: targetCompanyId }) 
      .then((all) => {
        if (cancelled) return;
        
        const validParents = all.filter((a) => {
          // 1. Normalize the asset type for comparison
          const rawType = a.assetType || "";
          const normalizedType = String(rawType).trim();
          
          // 2. Check if it can be a parent. We fallback to checking if the string includes "Parent"
          // just in case the exact match fails due to casing/spacing issues in your DB.
          let isEligibleType = false;
          try {
            isEligibleType = canBeParent(normalizedType) || normalizedType.toLowerCase().includes("parent");
          } catch (e) {
            console.warn("Error checking canBeParent for:", normalizedType, e);
          }
          
          // 3. Ensure it's not the current asset being edited
          const isNotSelf = !asset || a.id !== asset.id;

          // 4. Ensure it's not deleted
          const isNotDeleted = a?.metadata?.deletedFlag !== true;
          
          return isEligibleType && isNotSelf && isNotDeleted;
        });

        console.log("[Parent Assets Filter] Raw fetched:", all.length, "Valid parents:", validParents.length);
        setParentAssets(validParents);
      })
      .catch((err) => {
        console.warn("Parent assets load failed:", err);
        setParentAssets([]);
      })
      .finally(() => {
        if (!cancelled) setParentAssetsLoading(false);
      });
      
    return () => {
      cancelled = true;
    };
   
  }, [
    open, 
    formData.assetType, 
    currentUser?.companyId, 
    currentUser?.company,
    asset?.id
  ]);

  const scrollToField = useCallback((fieldName) => {
    const el = document.getElementById(fieldName);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => {
      try {
        if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
          el.focus();
        } else {
          const focusable =
            el.querySelector('[role="button"]') ||
            el.querySelector(
              'input:not([type="hidden"]), textarea, [tabindex]',
            );
          if (focusable) focusable.focus();
          else el.focus?.();
        }
      } catch {
        /* noop */
      }
    }, 450);
  }, []);

  

  useEffect(() => {
    if (!formData.subCategoryId) {
      setSubCategoryData(null);
      return;
    }
    const subCat = subCategories.find((sc) => sc.id === formData.subCategoryId);
    if (!subCat) return;
    setSubCategoryData(subCat);
    const defaults = getSubCategoryDepreciationConfig(subCat);
    setFormData((prev) => {
      const needsUpdate =
        !prev.depreciationMethod ||
        (!prev.depreciationRate && defaults.depreciationRate) ||
        (!prev.usefulLife && defaults.usefulLife);
      if (!needsUpdate) return prev;
      return {
        ...prev,
        depreciationMethod:
          prev.depreciationMethod || defaults.depreciationMethod,
        usefulLife: prev.usefulLife || defaults.usefulLife,
        depreciationRate: prev.depreciationRate || defaults.depreciationRate,
        isDepreciationAuto: prev.isDepreciationAuto !== false,
      };
    });
  }, [formData.subCategoryId, subCategories]);

  const applySubCategoryToAsset = useCallback(
    (selectedSubCat, subCategoryId) => {
      const depDefaults = getSubCategoryDepreciationConfig(selectedSubCat);
   if (depDefaults.depreciationMethod) {
        const upper = String(depDefaults.depreciationMethod).toUpperCase();
        if (upper === "SLM" || upper === "STRAIGHT-LINE") {
          depDefaults.depreciationMethod = "SLM";
        } else if (upper === "WDV" || upper === "WRITTEN-DOWN-VALUE") {
          depDefaults.depreciationMethod = "WDV";
        } else if (upper === "NONE") {
          depDefaults.depreciationMethod = "None";
        }
      }
     
      const subCatTrackingMethod =
        selectedSubCat?.assetTrackingMethod ||
        selectedSubCat?.trackingMethod ||
        selectedSubCat?.serializationType ||
        "";

      const newUserTrackingMode =
        subCatTrackingMethod === "non-serialized" ? "quantity" : "serialized";

      setSubCategoryData(selectedSubCat || null);
      setFormData((prev) => ({
        ...prev,
        subCategoryId,
        subCategoryName: selectedSubCat?.subCategoryName || "",
        ...depDefaults,
        isDepreciationAuto: true,
        residualValue: prev.residualValue || "",
        userTrackingMode: subCatTrackingMethod
          ? newUserTrackingMode
          : prev.userTrackingMode,
        manufacturerSerialNumber:
          subCatTrackingMethod === "non-serialized"
            ? ""
            : prev.manufacturerSerialNumber,
      }));
    },
    [],
  );

  const handleAssetTypeChange = useCallback((newType) => {
    setInheritedFromParent(null);
    setSubCategoryData(null);
    setFormData((prev) => {
      const cfg = getAssetTypeConfig(newType);
      const newSerialPolicy = cfg.serialPolicy;
      const newTracksQty = cfg.tracksQuantity;

      const newTrackingMode =
        newSerialPolicy === "disabled"
          ? "quantity"
          : newSerialPolicy === "required"
            ? "serialized"
            : "serialized";

      return {
        ...prev,
        assetType: newType,
        subCategoryId: "",
        subCategoryName: "",
        brandId: "",
        brandName: "",
        depreciationMethod: "",
        usefulLife: "",
        depreciationRate: "",
        isDepreciationAuto: true,

        parentAssetId: cfg.canBeParent ? "" : prev.parentAssetId,
        parentAssetName: cfg.canBeParent ? "" : prev.parentAssetName,
        purchasedSeparately:
          !cfg.canBeParent &&
          cfg.hasProcurementInheritance &&
          getOwnershipTypeConfig(prev.ownershipType).showsFinancialSection &&
          Boolean(prev.parentAssetId)
            ? prev.purchasedSeparately
            : false,
        manufacturerSerialNumber:
          newSerialPolicy === "disabled" ? "" : prev.manufacturerSerialNumber,

        quantity: newTracksQty ? prev.quantity || "1" : "1",
        unitOfMeasurement: newTracksQty ? prev.unitOfMeasurement : "",
        userTrackingMode: newTrackingMode,
      };
    });
      setErrors((prev) => ({
      ...prev,
      assetType: "",
      parentAssetId: "",
      subCategoryId: "",
    }));
  }, []);



const handleOwnershipTypeChange = useCallback(
  (newOwnership) => {
    const newFinancialShown =
      getOwnershipTypeConfig(newOwnership).showsFinancialSection;
    const isReturnOnly =
      newOwnership === "Rented" || newOwnership === "Leased";

    // Look up the current sub-category (from the live formData snapshot)
    const currentSubCat = subCategories.find(
      (sc) => sc.id === formData.subCategoryId
    );
    const method =
      currentSubCat?.assetTrackingMethod ||
      currentSubCat?.trackingMethod ||
      currentSubCat?.serializationType ||
      "";
    const alreadySerialized = method === "serialized";
    const shouldClear = isReturnOnly && !alreadySerialized;

    setFormData((prev) => ({
      ...prev,
      ownershipType: newOwnership,

      // Existing lease/rent resets
      leaseStartDate: "",
      leaseEndDate: "",
      leaseAmount: "",
      leaseVendorId: "",
      leaseVendorName: "",
      leaseAgreementNumber: "",
      leaseBillingFrequency: "",
      rentStartDate: "",
      expectedReturnDate: "",
      rentAmount: "",
      rentVendorId: "",
      rentVendorName: "",
      rentAgreementNumber: "",
      rentBillingFrequency: "",
      purchasedSeparately:
        newFinancialShown && Boolean(prev.parentAssetId)
          ? prev.purchasedSeparately
          : false,

      // Only clear if the current pick isn't serialized
      ...(shouldClear && {
        categoryId: "",
        categoryName: "",
        categoryCode: "",
        subCategoryId: "",
        subCategoryName: "",
        brandId: "",
        brandName: "",
      }),

      // Force serialized tracking for Rented/Leased
      ...(isReturnOnly && {
        userTrackingMode: "serialized",
        quantity: "1",
        unitOfMeasurement: "",
      }),
    }));

    if (shouldClear) setSubCategoryData(null);
    setErrors({});
  },
  [subCategories, formData.subCategoryId]
);

  const handleParentAssetChange = useCallback(
    (parentId) => {
      const parent = parentAssets.find((a) => a.id === parentId) || null;
      setInheritedFromParent(parent);

      setFormData((prev) => {
        const financialShown = getOwnershipTypeConfig(
          prev.ownershipType,
        ).showsFinancialSection;

        const willInheritProcurement =
          getAssetTypeConfig(prev.assetType).hasProcurementInheritance &&
          financialShown &&
          !prev.purchasedSeparately &&
          parent;

        const willInheritLocation =
          getAssetTypeConfig(prev.assetType).hasLocationInheritance && parent;

        return {
          ...prev,
          parentAssetId: parentId,
          parentAssetName: parent?.assetName || "",
          purchasedSeparately: parentId ? prev.purchasedSeparately : false,

          ...(willInheritProcurement && {
            purchaseDate: formatFormDate(
              parent.purchaseDate || parent.purchasedDate,
            ),
            purchaseValue: parent.purchaseValue?.toString() || "",
            vendorId: parent.vendorId || "",
            vendorName: parent.vendorName || "",
            warrantyExpiry: formatFormDate(parent.warrantyExpiry),
            invoiceNumber: parent.invoiceNumber || "",
            invoiceFileUrl: parent.invoiceFileUrl || "",
            invoiceFileName: parent.invoiceFileName || "",
            openingBookValue: parent.purchaseValue?.toString() || "",
          }),

          // REMOVED: Location inheritance - only keep locationCode
          ...(willInheritLocation && {
            locationCode: parent.locationCode || "",
            assignedDepartmentId: parent.assignedDepartmentId || "",
            assignedDepartmentName: parent.assignedDepartmentName || "",
            assignedTo: parent.assignedTo || "",
            assignedToName: parent.assignedToName || "",
          }),
        };
      });
    },
    [parentAssets],
  );

  const handlePurchasedSeparatelyChange = useCallback(
    (checked) => {
      setFormData((prev) => {
        if (!checked) {
          if (inheritedFromParent) {
            return {
              ...prev,
              purchasedSeparately: false,
              purchaseDate: formatFormDate(
                inheritedFromParent.purchaseDate ||
                  inheritedFromParent.purchasedDate,
              ),
              purchaseValue:
                inheritedFromParent.purchaseValue?.toString() || "",
              vendorId: inheritedFromParent.vendorId || "",
              vendorName: inheritedFromParent.vendorName || "",
              warrantyExpiry: formatFormDate(
                inheritedFromParent.warrantyExpiry,
              ),
              invoiceNumber: inheritedFromParent.invoiceNumber || "",
              invoiceFileUrl: inheritedFromParent.invoiceFileUrl || "",
              invoiceFileName: inheritedFromParent.invoiceFileName || "",
              openingBookValue:
                inheritedFromParent.purchaseValue?.toString() || "",
            };
          }
          return { ...prev, purchasedSeparately: false };
        }
        return {
          ...prev,
          purchasedSeparately: true,
          purchaseDate: "",
          purchaseValue: "",
          vendorId: "",
          vendorName: "",
          warrantyExpiry: "",
          invoiceNumber: "",
          invoiceFile: null,
          invoiceFileUrl: "",
          invoiceFileName: "",
          openingBookValue: "",
          isOpeningBookValueManual: false,
        };
      });
    },
    [inheritedFromParent],
  );

  const handleDepreciationChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value, isDepreciationAuto: false };
      if (name === "depreciationMethod") {
        if (value === DEPRECIATION_METHODS.SLM) next.depreciationRate = "";
        if (value === DEPRECIATION_METHODS.WDV) {
          next.usefulLife = "";
          next.residualValue = "0";
        }
        if (value === "") {
          next.usefulLife = "";
          next.depreciationRate = "";
        }
      }
      return next;
    });
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
  }, []);

  const handleResetToDefault = useCallback(() => {
    if (!subCategoryData) return;
    const defaults = getSubCategoryDepreciationConfig(subCategoryData);
    setFormData((prev) => ({ ...prev, ...defaults, isDepreciationAuto: true }));
  }, [subCategoryData]);

  const handleOpeningBookValueChange = useCallback((e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      openingBookValue: value,
      isOpeningBookValueManual: true,
    }));
    setErrors((prev) =>
      prev.openingBookValue ? { ...prev, openingBookValue: "" } : prev,
    );
  }, []);

  const handleResetOpeningBookValue = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      openingBookValue: prev.purchaseValue || "",
      isOpeningBookValueManual: false,
    }));
  }, []);

  const handleFileInputChange = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    const allowedTypes = [
      "image/png",
      "image/jpeg",
      "image/webp",
      "application/pdf",
    ];
    if (selectedFile.size > MAX_FILE_SIZE) {
      setSubmitError("File size exceeds 750 KB limit.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (!allowedTypes.includes(selectedFile.type)) {
      setSubmitError("Only PNG, JPG, JPEG, WEBP, and PDF files are allowed.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setSubmitError("");
    setFormData((prev) => ({
      ...prev,
      invoiceFile: selectedFile,
      invoiceFileName: selectedFile.name,
      invoiceFileUrl: "",
    }));
  }, []);

  const handleChooseFileClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  }, []);

  const handleClearFile = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      invoiceFile: null,
      invoiceFileName: "",
      invoiceFileUrl: "",
    }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  // ── Sales Invoice Upload handlers ──────────────────────────
  const handleSalesFileInputChange = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    const allowedTypes = [
      "image/png",
      "image/jpeg",
      "image/webp",
      "application/pdf",
    ];
    if (selectedFile.size > MAX_FILE_SIZE) {
      setSubmitError("Sales invoice file size exceeds 750 KB limit.");
      if (salesFileInputRef.current) salesFileInputRef.current.value = "";
      return;
    }
    if (!allowedTypes.includes(selectedFile.type)) {
      setSubmitError(
        "Only PNG, JPG, JPEG, WEBP, and PDF files are allowed for sales invoice.",
      );
      if (salesFileInputRef.current) salesFileInputRef.current.value = "";
      return;
    }
    setSubmitError("");
    setFormData((prev) => ({
      ...prev,
      salesInvoiceFile: selectedFile,
      salesInvoiceFileName: selectedFile.name,
      salesInvoiceFileUrl: "",
    }));
  }, []);

  const handleChooseSalesFileClick = useCallback(() => {
    if (salesFileInputRef.current) {
      salesFileInputRef.current.value = "";
      salesFileInputRef.current.click();
    }
  }, []);

  const handleClearSalesFile = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      salesInvoiceFile: null,
      salesInvoiceFileName: "",
      salesInvoiceFileUrl: "",
    }));
    if (salesFileInputRef.current) salesFileInputRef.current.value = "";
  }, []);

  const handleTrackingModeChange = useCallback((mode) => {
    setFormData((prev) => ({
      ...prev,
      userTrackingMode: mode,
      manufacturerSerialNumber:
        mode === "serialized" ? prev.manufacturerSerialNumber : "",
      quantity: mode === "quantity" ? prev.quantity || "1" : "1",
    }));
    setErrors((prev) => ({
      ...prev,
      manufacturerSerialNumber: "",
      quantity: "",
    }));
  }, []);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setSubmitError("");

      if (name === "assetType") {
        handleAssetTypeChange(value);
        return;
      }
      if (name === "ownershipType") {
        handleOwnershipTypeChange(value);
        return;
      }
      if (name === "parentAssetId") {
        handleParentAssetChange(value);
        return;
      }

      if (name === "locationCode") {
        // REMOVED: All location field population - only store the code
        setFormData((prev) => ({
          ...prev,
          locationCode: value,
        }));
      } else if (name === "categoryId") {
        const cat = categories.find((c) => c.id === value);
        setFormData((prev) => ({
          ...prev,
          categoryId: value,
          categoryName: cat?.categoryName || "",
          categoryCode: cat?.categoryId || "",
          subCategoryId: "",
          subCategoryName: "",
          brandId: "",
          brandName: "",
          depreciationMethod: "",
          usefulLife: "",
          depreciationRate: "",
          isDepreciationAuto: true,
        }));
        setSubCategoryData(null);
      } else if (name === "subCategoryId") {
        const sub = subCategories.find((s) => s.id === value);
        applySubCategoryToAsset(sub, value);
      } else if (name === "brandId") {
        const brand = brands.find((b) => b.id === value);
        setFormData((prev) => ({
          ...prev,
          brandId: value,
          brandName: brand?.name || brand?.brandName || "",
        }));
            } else if (name === "customerId") {
        const cust = customers.find((c) => c.id === value);
        setFormData((prev) => ({
          ...prev,
          customerId: value,
          customerName: cust?.customerName || "",
        }));
      } else if (name === "vendorId") {
        const vendor = vendors.find((v) => v.id === value);
        setFormData((prev) => ({
          ...prev,
          vendorId: value,
          vendorName: vendor?.vendorName || "",
        }));
      } else if (name === "leaseVendorId") {
        const vendor = vendors.find((v) => v.id === value);
        setFormData((prev) => ({
          ...prev,
          leaseVendorId: value,
          leaseVendorName: vendor?.vendorName || "",
        }));
      } else if (name === "rentVendorId") {
        const vendor = vendors.find((v) => v.id === value);
        setFormData((prev) => ({
          ...prev,
          rentVendorId: value,
          rentVendorName: vendor?.vendorName || "",
        }));
      } else if (name === "assignedDepartmentId") {
        const dept = departments.find((d) => d.id === value);
        setFormData((prev) => ({
          ...prev,
          assignedDepartmentId: value,
          assignedDepartmentName: dept?.departmentName || dept?.name || "",
        }));
      } else if (name === "assignedTo") {
        const user = users.find((u) => u.id === value);
        setFormData((prev) => {
          const preservedStatuses = ["In Maintenance"];
          const newStatus = value
            ? preservedStatuses.includes(prev.status)
              ? prev.status
              : "Assigned"
            : prev.status === "Assigned"
              ? "Available"
              : prev.status;
          return {
            ...prev,
            assignedTo: value,
            assignedToName: user?.displayName || user?.name || "",
            status: newStatus,
          };
        });
      } else {
        setFormData((prev) => {
          const updated = { ...prev, [name]: value };
          if (
            name === "purchaseValue" &&
            !prev.isOpeningBookValueManual &&
            !isEditMode
          ) {
            updated.openingBookValue = value;
          }
          return updated;
        });
      }

      setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
    },
    [
      locations,
      categories,
      subCategories,
      brands,
      vendors,
      departments,
      users,
      applySubCategoryToAsset,
      isEditMode,
      handleAssetTypeChange,
      handleOwnershipTypeChange,
      handleParentAssetChange,
    ],
  );

    // ── Handlers for SearchableDropdown ──────────────────────────────────────
 
  const handleSearchableChange = useCallback((e) => {
    const { name, value } = e.target;
    setSubmitError("");

    // ─────────── CUSTODY (Assigned To) ───────────
    if (name === "assignedTo") {
      const user = users.find((u) => u.id === value);
      setFormData((prev) => {
        const preservedStatuses = ["In Maintenance"];
        const newStatus = value
          ? preservedStatuses.includes(prev.status)
            ? prev.status
            : "Assigned"
          : prev.status === "Assigned"
            ? "Available"
            : prev.status;
        return {
          ...prev,
          assignedTo: value,
          assignedToName: user?.displayName || user?.name || "",
          status: newStatus,
        };
      });
    }

    // ─────────── LOCATION ───────────
    else if (name === "locationCode") {
      setFormData((prev) => ({
        ...prev,
        locationCode: value,
      }));
    }

    // ─────────── ASSET OWNER (users collection only) ───────────
    else if (name === "ownerId") {
      const owner = users.find((u) => u.id === value);
      setFormData((prev) => ({
        ...prev,
        ownerId: value,
        ownerName: owner?.displayName || owner?.name || "",
      }));
    }

   // ─────────── APPROVAL WORKFLOW (per-level selects) ───────────
  else if (name && name.startsWith("approverLevel_")) {
    const level = Number(name.replace("approverLevel_", ""));
    const entry = approverOptionsByLevel.get(level);

    setFormData((prev) => {
      const list = Array.isArray(prev.approverList) ? [...prev.approverList] : [];
      const idx = list.findIndex((r) => Number(r.level) === level);

      // Extract emails correctly from the Autocomplete array of objects
      const selectedEmails = Array.isArray(value)
        ? value.map((u) => (typeof u === "string" ? u : u.value)).filter(Boolean)
        : (value ? [value] : []);

               const prior = idx >= 0 ? list[idx] : null;
      const priorStatus = String(prior?.status || "").toLowerCase();
      const isApproved = priorStatus.startsWith("approved");
      const isRework =
        priorStatus.includes("return") ||
        priorStatus.includes("rejected") ||
        priorStatus.includes("correction");

      // Detect whether the creator actually changed the set of approvers.
      const prevSet = [...(prior?.users || [])].sort().join("|");
      const nextSet = [...selectedEmails].sort().join("|");
      const reassigned = prevSet !== nextSet;

        const row = {
        level,
        label: entry?.label || `Level ${level}`,
        users: selectedEmails,
        currentIndex: isApproved
          ? (prior?.currentIndex ?? 0)
          : (reassigned || isRework ? 0 : (prior?.currentIndex ?? 0)),
        actedBy: isApproved
          ? (prior?.actedBy || null)
          : (reassigned ? null : (prior?.actedBy || null)),
        actedAt: isApproved
          ? (prior?.actedAt || null)
          : (reassigned ? null : (prior?.actedAt || null)),
        // On reassign, drop the previously-approved users for this level.
        approvedUsers: isApproved
          ? (Array.isArray(prior?.approvedUsers) ? prior.approvedUsers : [])
          : (reassigned || isRework
              ? []
              : (Array.isArray(prior?.approvedUsers) ? prior.approvedUsers : [])),
        status: isApproved
          ? prior.status
          : (reassigned || isRework
              ? `Pending Level ${level}`
              : (prior?.status || `Pending Level ${level}`)),
        email: selectedEmails[0] || null,
        name: null,
      };

      if (idx >= 0) list[idx] = row;
      else list.push(row);

      list.sort((a, b) => Number(a.level) - Number(b.level));
      return { ...prev, approverList: list };
    });
  }
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
  }, [users, approverOptions]);

  const validateForm = useCallback(() => {
    const fd = formDataRef.current;

    const _assetCfg = getAssetTypeConfig(fd.assetType);
    const _ownershipCfg = getOwnershipTypeConfig(fd.ownershipType);

    const _serialPolicy = resolveEffectiveSerialPolicy(
      fd.assetType,
      subCategoryData,
    );
    const _allowToggle = _serialPolicy === "optional";
    const _isSerialized =
      _serialPolicy === "required"
        ? true
        : _serialPolicy === "disabled"
          ? false
          : fd.userTrackingMode === "serialized";
      const _showSerial = _serialPolicy !== "disabled";
    const _serialReq = _serialPolicy === "required";
    const _showQtyFields =
      fd.userTrackingMode !== "serialized" || _assetCfg.tracksQuantity;
    const _hasExpiryDate = _assetCfg.hasExpiryDate;
    const _requiresParent = _assetCfg.requiresParent;

    const _showsFinancial = _ownershipCfg.showsFinancialSection;
    const _showsLease = _ownershipCfg.showsLeaseFields;
    const _showsRental = _ownershipCfg.showsRentalFields;
    const _requiresBilling = _ownershipCfg.requiresBillingFrequency;
    const _showsDepr = _ownershipCfg.showsDepreciation;
     const _requiresApproval =
      approverEnabled &&
      String(currentUser?.role || "").toLowerCase() !== "admin" &&
      // Skip for legacy assets being edited — they never had approvers
      (!isEditMode ||
        (Array.isArray(asset?.approverList) && asset.approverList.length > 0));
    const {
      assetName,
      categoryId,
      subCategoryId,
      brandId,
      status,
      locationCode,
      depreciationMethod,
      residualValue,
      openingBookValue,
      usefulLife,
      depreciationRate,
      manufacturerSerialNumber,
      quantity,
      assetType,
      ownershipType,
      parentAssetId,
      purchaseDate,
      purchaseValue,
      vendorId,
      leaseStartDate,
      leaseEndDate,
      leaseAmount,
      leaseVendorId,
      leaseAgreementNumber,
      leaseBillingFrequency,
      rentStartDate,
      expectedReturnDate,
      rentAmount,
      rentVendorId,
      rentAgreementNumber,
      rentBillingFrequency,
      expiryDate,
    } = fd;

    const newErrors = {};
    const numericPurchaseValue = toNumber(purchaseValue);

    if (!assetType) newErrors.assetType = "Asset Type is required";
    if (!ownershipType) newErrors.ownershipType = "Ownership Type is required";
    if (!assetName) newErrors.assetName = "Asset Name is required";
    if (!categoryId) newErrors.categoryId = "Category is required";
    if (!subCategoryId) newErrors.subCategoryId = "Sub-Category is required";
    if (!brandId) newErrors.brandId = "Brand is required";
    if (!status) newErrors.status = "Status is required";
    if (!locationCode) newErrors.locationCode = "Location is required";

      if (_requiresParent && !parentAssetId) {
      if (parentAssets.length === 0 && !parentAssetsLoading) {
        newErrors.parentAssetId = `No eligible Primary assets exist. You must create a Primary asset first before adding an ${assetType}.`;
      } else {
        newErrors.parentAssetId = `Primary Asset is required for ${assetType}.`;
      }
    }

       if (fd.assetType === "Customer") {
      if (!fd.salesDate) newErrors.salesDate = "Sales Date is required";
      if (!fd.salesValue || toNumber(fd.salesValue) <= 0) newErrors.salesValue = "Valid Sales Value is required";
      if (!fd.customerId) newErrors.customerId = "Primary Customer is required";
    }

    if (_showsFinancial) {
      if (!purchaseDate) newErrors.purchaseDate = "Purchase Date is required";
      if (!purchaseValue)
        newErrors.purchaseValue = "Purchase Value is required";
      else if (numericPurchaseValue <= 0)
        newErrors.purchaseValue = "Purchase Value must be greater than zero";
      if (!vendorId) newErrors.vendorId = "Vendor is required";
    }

    if (_showsLease) {
      if (!leaseStartDate)
        newErrors.leaseStartDate = "Lease Start Date is required";
      if (!leaseEndDate) newErrors.leaseEndDate = "Lease End Date is required";
      if (
        leaseStartDate &&
        leaseEndDate &&
        new Date(leaseEndDate) <= new Date(leaseStartDate)
      )
        newErrors.leaseEndDate =
          "Lease End Date must be after Lease Start Date";
      if (!leaseAmount || toNumber(leaseAmount) <= 0)
        newErrors.leaseAmount = "Lease Amount must be greater than zero";
      if (!leaseVendorId)
        newErrors.leaseVendorId = "Leasing Company is required";
      if (!leaseAgreementNumber)
        newErrors.leaseAgreementNumber = "Lease Agreement Number is required";
      if (_requiresBilling && !leaseBillingFrequency)
        newErrors.leaseBillingFrequency = "Billing Frequency is required";
    }

    if (_showsRental) {
      if (!rentStartDate)
        newErrors.rentStartDate = "Rent Start Date is required";
      if (!expectedReturnDate)
        newErrors.expectedReturnDate = "Expected Return Date is required";
      if (
        rentStartDate &&
        expectedReturnDate &&
        new Date(expectedReturnDate) <= new Date(rentStartDate)
      )
        newErrors.expectedReturnDate =
          "Return Date must be after Rent Start Date";
      if (!rentAmount || toNumber(rentAmount) <= 0)
        newErrors.rentAmount = "Rent Amount must be greater than zero";
      if (!rentVendorId) newErrors.rentVendorId = "Rental Company is required";
      if (!rentAgreementNumber)
        newErrors.rentAgreementNumber = "Rent Agreement Number is required";
      if (_requiresBilling && !rentBillingFrequency)
        newErrors.rentBillingFrequency = "Billing Frequency is required";
    }

    if (_showsDepr) {
      if (depreciationMethod === DEPRECIATION_METHODS.SLM) {
        const parsedResidual = parseOptionalNumber(residualValue);
        if (parsedResidual !== null) {
          if (Number.isNaN(parsedResidual) || parsedResidual < 0)
            newErrors.residualValue = "Residual Value must be zero or greater";
          else if (
            numericPurchaseValue > 0 &&
            parsedResidual > numericPurchaseValue
          )
            newErrors.residualValue =
              "Residual Value cannot exceed Purchase Value";
        }
        if (!usefulLife)
          newErrors.usefulLife = "Useful Life is required for SLM";
        else if (toNumber(usefulLife) <= 0)
          newErrors.usefulLife = "Useful Life must be greater than zero";
      }
      if (depreciationMethod === DEPRECIATION_METHODS.WDV) {
        const parsedResidual = parseOptionalNumber(residualValue);
        if (parsedResidual !== null && parsedResidual !== 0)
          newErrors.residualValue = "Residual Value must be ₹0 for WDV method";
        const parsedObv = parseOptionalNumber(openingBookValue);
        if (parsedObv !== null) {
          if (Number.isNaN(parsedObv) || parsedObv <= 0)
            newErrors.openingBookValue =
              "Opening Book Value must be greater than zero";
          else if (numericPurchaseValue > 0 && parsedObv > numericPurchaseValue)
            newErrors.openingBookValue =
              "Opening Book Value cannot exceed Purchase Value";
        }
        if (!depreciationRate)
          newErrors.depreciationRate = "Depreciation Rate is required for WDV";
        else if (
          toNumber(depreciationRate) <= 0 ||
          toNumber(depreciationRate) > 100
        )
          newErrors.depreciationRate =
            "Depreciation Rate must be between 0 and 100";
      }
    }

   const _isReturnOnly =
  fd.ownershipType === "Rented" || fd.ownershipType === "Leased";

if (
  (_showSerial || _isReturnOnly) &&
  (_isSerialized || _isReturnOnly) &&
  (_serialReq || _isReturnOnly) &&
  !manufacturerSerialNumber
) {
  newErrors.manufacturerSerialNumber = "Serial Number is required";
}
    if (_showQtyFields && (!quantity || toNumber(quantity) <= 0)) {
      newErrors.quantity = "A valid positive quantity is required";
    }
    if (_hasExpiryDate && !expiryDate) {
      newErrors.expiryDate = "Expiry Date is required for this asset type";
    }
       if (_requiresApproval) {
      if (approverLevels.length === 0) {
        newErrors.approverLevel_1 =
          "No approvers configured. Ask an admin to set up the Approver catalog.";
      } else {
  const listByLevel = new Map(
    (fd.approverList || []).map((r) => [Number(r.level), r]),
  );

  for (const lvl of approverLevels) {
    const entry = approverOptionsByLevel.get(lvl);
    const row = listByLevel.get(lvl);

    if (!entry || (entry.users || []).length === 0) {
      newErrors[`approverLevel_${lvl}`] =
        `No users assigned to "${entry?.label || `Level ${lvl}`}".`;
      continue;
    }

    if (!row) {
      newErrors[`approverLevel_${lvl}`] =
        `${entry.label || `Level ${lvl}`} is missing from the approver list — reopen the modal.`;
      continue;
    }

    const rowUsers =
      Array.isArray(row.users) && row.users.length > 0
        ? row.users
        : (row.email ? [row.email] : []);

           if (rowUsers.length === 0) {
          newErrors[`approverLevel_${lvl}`] =
            `Please select at least 1 person for ${entry.label || `Level ${lvl}`}.`;
        }
  }
} 
}
    setErrors(newErrors);
    const errorKeys = Object.keys(newErrors);
    if (errorKeys.length > 0) {
      const count = errorKeys.length;
      setSubmitError(
        `Please fix ${count} ${count === 1 ? "error" : "errors"} highlighted below before submitting.`,
      );
      setTimeout(() => scrollToField(errorKeys[0]), 100);
    }
   return errorKeys.length === 0;
  }, [scrollToField, subCategoryData, approverEnabled, currentUser, approverLevels, approverOptionsByLevel, asset, isEditMode]);
   const handleSubmit = useCallback(
    async (e) => {
      if (e && e.preventDefault) e.preventDefault();

      if (isSubmissionBlockedByApproval) {
        setSubmitError(
          `Cannot save: asset is in "${assetApprovalLabel}" status. ` +
          `Editing is only allowed in Draft or Returned status.`,
        );
        return;
      }

      if (!validateForm()) return;
      setLoading(true);
      setSubmitError("");

      const fd = formDataRef.current;

      try {
        const _ownershipCfg = getOwnershipTypeConfig(fd.ownershipType);
        const _assetCfg = getAssetTypeConfig(fd.assetType);
        const _showsFinancial = _ownershipCfg.showsFinancialSection;
        const _showsLease = _ownershipCfg.showsLeaseFields;
        const _showsRental = _ownershipCfg.showsRentalFields;
        const _showsDepr = _ownershipCfg.showsDepreciation;

        const _serialPolicy = resolveEffectiveSerialPolicy(
          fd.assetType,
          subCategoryData,
        );
        const _allowToggle = _serialPolicy === "optional";
        const _isSerialized =
          _serialPolicy === "required"
            ? true
            : _serialPolicy === "disabled"
              ? false
              : fd.userTrackingMode === "serialized";
               const _showSerial = _serialPolicy !== "disabled";
        const _showQtyFields =
          fd.userTrackingMode !== "serialized" || _assetCfg.tracksQuantity;
                const _hasReorder = _assetCfg.hasReorderLevel;
        const _hasExpiry = _assetCfg.hasExpiryDate;

        // ── Should we write approval metadata for this save? ──
        //   New asset + workflow enabled → yes
        //   Edit + asset already had an approverList → yes (normal re-work flow)
        //   Edit + asset was legacy (no approverList) → NO. Never "upgrade" it.
        const _shouldWriteApprovalMetadata =
          approverEnabled &&
          (!isEditMode ||
            (Array.isArray(asset?.approverList) && asset.approverList.length > 0));
        let finalInvoiceFileUrl = fd.invoiceFileUrl;
        if (fd.invoiceFile) {
          try {
            finalInvoiceFileUrl = await uploadWithTimeout(
              repo.uploadFile(
                fd.invoiceFile,
                `invoices/${currentUser.companyId}`,
              ),
            );
          } catch (uploadErr) {
            throw new Error(`Invoice upload failed: ${uploadErr.message}`);
          }
        }

        // ── Sales Invoice Upload (Customer Asset only) ──────────
        let finalSalesInvoiceFileUrl = fd.salesInvoiceFileUrl;
        if (fd.assetType === "Customer" && fd.salesInvoiceFile) {
          try {
            finalSalesInvoiceFileUrl = await uploadWithTimeout(
              repo.uploadFile(
                fd.salesInvoiceFile,
                `sales-invoices/${currentUser.companyId}`,
              ),
            );
          } catch (uploadErr) {
            throw new Error(
              `Sales invoice upload failed: ${uploadErr.message}`,
            );
          }
        }

        const targetDocId = isEditMode ? asset.id : generateUUID();

               // ── Direct Atomic Increment & Sequential ID Generation ───────────
        let activeAssetId = fd.assetId;

        let assetPref = null;
        let prefValue = {};

        if (!isEditMode) {
          assetPref = await preferencesService.getByPreferenceId(
            currentUser,
            "assets-id",
          );

          // Tolerant parse — string (canonical) OR object (some repo layers
          // return the field already parsed). This same parsed map is reused
          // in the write block below, so we never re-parse and never throw.
          const raw = assetPref?.preferenceValue;
          if (raw != null) {
            if (typeof raw === "string") {
              try {
                const p = JSON.parse(raw);
                if (p && typeof p === "object" && !Array.isArray(p)) {
                  prefValue = p;
                }
              } catch (e) {
                console.warn(
                  "[CreateEditAssetModal] preferenceValue parse failed:",
                  e,
                  raw,
                );
              }
            } else if (typeof raw === "object" && !Array.isArray(raw)) {
              prefValue = { ...raw };
            }
          }

          activeAssetId = prefValue[fd.categoryCode] || 1;
        }

        let generatedQrToken = isEditMode ? asset?.qrToken || "" : "";

        // If no existing token was found (or if not in edit mode), generate a new one and wait
        if (!generatedQrToken) {
          generatedQrToken = await generateQRToken(
            targetDocId,
              currentUser.userId || currentUser.id,
  currentUser.companyId,
          );
        }

        

        const purchaseValueNumber = toNumber(fd.purchaseValue);
        const residualValueNumber =
          fd.depreciationMethod === DEPRECIATION_METHODS.WDV
            ? 0
            : toNumber(fd.residualValue);
        const openingBookValueNumber =
          fd.depreciationMethod === DEPRECIATION_METHODS.WDV
            ? toNumber(fd.openingBookValue || fd.purchaseValue)
            : purchaseValueNumber;

        const assignedToChanged = isEditMode
          ? fd.assignedTo !== asset?.assignedTo
          : Boolean(fd.assignedTo);
        const now = new Date().toISOString();

        // Changed Ravi
        let aId = "";

        if (!isEditMode) {
          // Fall back to currentUser.companyId if assetPref is null (new company)
          const companyCode = assetPref?.companyId || currentUser.companyId || fd.companyId;
          
          aId =
            companyCode +
            "-" +
            fd.categoryCode +
            "-" +
            String(activeAssetId).padStart(5, "0");
        } else {
          aId = fd.assetId;
        }

        const computedPayload = {
          id: targetDocId,
          assetId: aId,
          assetName: fd.assetName,
          assetDescription: fd.assetDescription,
          companyId: fd.companyId,
                   purchaseValue: _showsFinancial ? purchaseValueNumber : null,
          purchaseDate: _showsFinancial ? fd.purchaseDate || null : null,
          
          // Sales & Warranty fields
          salesDate: fd.assetType === "Customer" ? fd.salesDate || null : null,
          salesValue: fd.assetType === "Customer" ? toNumber(fd.salesValue) : null,
          customerId: fd.assetType === "Customer" ? fd.customerId || null : null,
          customerName: fd.assetType === "Customer" ? fd.customerName || null : null,
          salesInvoiceNumber: fd.assetType === "Customer" ? fd.salesInvoiceNumber || null : null,
          salesInvoiceFileUrl:
            fd.assetType === "Customer"
              ? finalSalesInvoiceFileUrl || null
              : null,
          warrantyExpiry:
            fd.assetType === "Customer" || _showsFinancial
              ? fd.warrantyExpiry || null
              : null,
                    depreciationSchedule: Array.isArray(fd.depreciationSchedule)
            ? fd.depreciationSchedule
            : [],
                  invoiceNumber: _showsFinancial ? fd.invoiceNumber : null,
          invoiceFileUrl: _showsFinancial ? finalInvoiceFileUrl : null,
          vendorId: _showsFinancial ? fd.vendorId : null,
          locationCode: fd.locationCode,
          status: fd.status,
          assignedTo: fd.assignedTo,
          assignedToName: fd.assignedToName,
          assignedDepartmentId: fd.assignedDepartmentId,
          assignedDepartmentName: fd.assignedDepartmentName || null,
          ownerId: ownershipEnabled ? fd.ownerId || null : null,
          ownerName: ownershipEnabled ? fd.ownerName || null : null,
                                           // ── Approval──
          // If the asset was ever RETURNED / REJECTED / CORRECTION by any
          // level, re-submission restarts to Level 1 —
          // every approver row is reset to "Pending Level N".
          // Otherwise (normal edit) each row keeps its existing status.
                   approverList: _shouldWriteApprovalMetadata
  ? (() => {
      const existingList = Array.isArray(asset?.approverList)
        ? asset.approverList
        : [];
      const isReturnedOrRejected = existingList.some((r) => {
        const s = String(r?.status || "").toLowerCase();
        return (
          s.includes("return") ||
          s.includes("rejected") ||
          s.includes("correction")
        );
      });
      return (fd.approverList || []).map((row) => {
        const lvl = Number(row.level);
        const pendingStatus =
          lvl === 1 ? "Pending Level 1" : `Pending Level ${lvl}`;

       
            // Reconcile against the current SVL roster so removed users don't
        // keep receiving approval emails.
        const rosterEmails = new Set(
          (approverOptionsByLevel.get(lvl)?.users || [])
            .map((u) => String(u.value || "").trim().toLowerCase())
            .filter((e) => e.includes("@")),
        );
        const rawSelected =
          Array.isArray(row.users) && row.users.length > 0
            ? row.users
            : (row.email ? [row.email] : []);
        const selectedUsers = rawSelected
          .map((e) => String(e || "").trim().toLowerCase())
          .filter((e) => e.includes("@") && (rosterEmails.size === 0 || rosterEmails.has(e)));

        const priorApproved = Array.isArray(row.approvedUsers)
          ? row.approvedUsers.map((e) => String(e).toLowerCase())
          : [];
        // Drop approvedUsers that are no longer in the roster.
        const approvedUsers = isReturnedOrRejected
          ? []
          : priorApproved.filter(
              (e) => selectedUsers.includes(e) && (rosterEmails.size === 0 || rosterEmails.has(e)),
            );

        return {
          level: lvl,
          label: row.label || `Level ${lvl}`,
          users: selectedUsers,
          approvedUsers,
          currentIndex: isReturnedOrRejected
            ? 0
            : (Number.isFinite(row.currentIndex) ? row.currentIndex : 0),
          actedBy: isReturnedOrRejected ? null : (row.actedBy || null),
          actedAt: isReturnedOrRejected ? null : (row.actedAt || null),
          status: isReturnedOrRejected
            ? pendingStatus
            : row.status || pendingStatus,
          email: selectedUsers[0] || null,
          name: null,
        };
      });
    })()
  : [],
approvalStatus: _shouldWriteApprovalMetadata
  ? deriveApprovalStatus(
      (fd.approverList || []).map((row) => {
        const lvl = Number(row.level);
        const pendingStatus =
          lvl === 1 ? "Pending Level 1" : `Pending Level ${lvl}`;

        const existingList = Array.isArray(asset?.approverList)
          ? asset.approverList
          : [];
        const isReturnedOrRejected = existingList.some((r) => {
          const s = String(r?.status || "").toLowerCase();
          return (
            s.includes("return") ||
            s.includes("rejected") ||
            s.includes("correction")
          );
        });

        return {
          level: lvl,
          status: isReturnedOrRejected
            ? pendingStatus               
            : row.status || pendingStatus,
        };
      }),
    )
   : "NA",
          // approvalHistory log of actions.
     approvalHistory: _shouldWriteApprovalMetadata
  ? (isEditMode
      ? Array.isArray(asset?.approvalHistory)
        ? asset.approvalHistory
        : []
      : [
          {
            level: 0,
            status: "Draft",
            createdBy: currentUser?.id || currentUser?.uid || null,
            createdByEmail: (currentUser?.email || "").toLowerCase(),
            createdAt: new Date().toISOString(),
          },
        ])
           : (Array.isArray(asset?.approvalHistory) ? asset.approvalHistory : []),
          approverEmails: _shouldWriteApprovalMetadata
            ? Array.from(
                new Set(
                  (fd.approverList || []).flatMap((row) => {
                    const list =
                      Array.isArray(row.users) && row.users.length > 0
                        ? row.users
                        : (row.email ? [row.email] : []);
                    return list
                      .map((e) => String(e || "").trim().toLowerCase())
                      .filter((e) => e.includes("@"));
                  }),
                ),
              )
            : [],

          assignedOn: fd.assignedTo
            ? assignedToChanged
              ? now
              : asset?.assignedOn || now
            : null,

          categoryId: fd.categoryId,
          subCategoryId: fd.subCategoryId,
          brandId: fd.brandId,
          maintenanceFrequency: fd.maintenanceFrequency,
          maintenanceStartDate: fd.maintenanceStartDate || null,

          serializationType:
  fd.ownershipType === "Rented" || fd.ownershipType === "Leased"
    ? "serialized"
    : _isSerialized
      ? "serialized"
      : "non-serialized",
      
          manufacturerSerialNumber:
            _showSerial && _isSerialized ? fd.manufacturerSerialNumber : null,
          quantity: _showQtyFields ? toNumber(fd.quantity) : 1,
          unitOfMeasurement: _showQtyFields ? fd.unitOfMeasurement : null,
          reorderLevel: _hasReorder ? toNumber(fd.reorderLevel) || null : null,
          expiryDate: _hasExpiry ? fd.expiryDate || null : null,

                qrToken: generatedQrToken,

          depreciationMethod: _showsDepr ? fd.depreciationMethod || null : null,
          usefulLife:
            _showsDepr && fd.depreciationMethod === DEPRECIATION_METHODS.SLM
              ? parseInt(fd.usefulLife, 10)
              : null,
          depreciationRate:
            _showsDepr && fd.depreciationMethod === DEPRECIATION_METHODS.WDV
              ? toNumber(fd.depreciationRate)
              : null,
          residualValue: _showsDepr ? residualValueNumber : null,
          openingBookValue: _showsDepr ? openingBookValueNumber : null,
          isOpeningBookValueManual: fd.isOpeningBookValueManual,
          isDepreciationAuto: fd.isDepreciationAuto,

          assetType: fd.assetType || "",
          ownershipType: fd.ownershipType || "",
          parentAssetId: fd.parentAssetId || null,
          parentAssetName: fd.parentAssetName || null,
          purchasedSeparately: fd.purchasedSeparately ?? false,

          ...(_showsLease && {
            leaseStartDate: fd.leaseStartDate || null,
            leaseEndDate: fd.leaseEndDate || null,
            leaseAmount: toNumber(fd.leaseAmount),
            leaseVendorId: fd.leaseVendorId || null,
            leaseVendorName: fd.leaseVendorName || "",
            leaseAgreementNumber: fd.leaseAgreementNumber || "",
            leaseBillingFrequency: fd.leaseBillingFrequency || "",
          }),

          ...(_showsRental && {
            rentStartDate: fd.rentStartDate || null,
            expectedReturnDate: fd.expectedReturnDate || null,
            rentAmount: toNumber(fd.rentAmount),
            rentVendorId: fd.rentVendorId || null,
            rentVendorName: fd.rentVendorName || "",
            rentAgreementNumber: fd.rentAgreementNumber || "",
            rentBillingFrequency: fd.rentBillingFrequency || "",
          }),
        };

             Object.keys(computedPayload).forEach((key) => {
          if (computedPayload[key] === undefined) delete computedPayload[key];
        });

                    // Sanity check: for a new asset with approval enabled,
        // every configured level must be present in approverList.
      if (approverEnabled && !isEditMode) {
  const list = Array.isArray(computedPayload.approverList)
    ? computedPayload.approverList
    : [];
  const missingLevels = approverLevels.filter((lvl) => {
    const row = list.find((r) => Number(r.level) === lvl);
    if (!row) return true;
    const rowUsers = Array.isArray(row.users) && row.users.length > 0
      ? row.users
      : (row.email ? [row.email] : []);
    return rowUsers.length === 0;
  });
  if (missingLevels.length > 0) {
    console.error(
      "[CreateEditAssetModal] Refusing to create asset — missing approver levels:",
      missingLevels,
      "computedPayload.approverList =",
      computedPayload.approverList,
    );
    throw new Error(
      `Approver setup incomplete for levels: ${missingLevels.join(", ")}. ` +
        `Please reopen the modal and re-select approvers.`,
    );
  }
}

        await assetService.upsertData(targetDocId, computedPayload, {
          isCreation: !isEditMode,
        });
            if (!isEditMode) {
          // Bump exactly one key in the map we parsed in the read block.
          // No re-parsing here — that's what used to silently overwrite
          // the whole preferenceValue when the repo returned an object.
          prefValue[fd.categoryCode] = activeAssetId + 1;
          const newPrefValueString = JSON.stringify(prefValue);
          const nowIso = new Date().toISOString();

          if (assetPref && assetPref.id) {
            // Existing preference → mutate the SAME doc and write it back
            // through preferencesService.upsert (identical to import).
            // Preserves scope, userId, createdAt, createdBy, deleted, and
            // any custom fields the doc may carry.
            const updatedPref = {
              ...assetPref,
              preferenceValue: newPrefValueString,
              updatedAt: nowIso,
              updatedBy: currentUser?.email || "system",
              updated_at: nowIso,
            };
            await preferencesService.upsert(
              currentUser,
              updatedPref.id,
              updatedPref,
            );
          } else {
            // Fresh company — create the exact schema import would create,
            // so the two paths converge from the very first write.
            const prefDocId = `${currentUser.companyId}AssetPreference`;
            const freshPref = {
              companyId: currentUser.companyId,
              deleted: false,
              id: prefDocId,
              preferenceId: "assets-id",
              preferenceName: "assets_id",
              preferenceType: "__system__",
              preferenceValue: newPrefValueString,
              scope: null,
              createdAt: nowIso,
              createdBy: currentUser?.email || "system",
              updatedAt: nowIso,
              updatedBy: currentUser?.email || "system",
              updated_at: nowIso,
              userId: null,
            };
            await preferencesService.upsert(
              currentUser,
              freshPref.id,
              freshPref,
            );
          }
        }

const _wasReturnedOrRejected = (() => {
  const existing = Array.isArray(asset?.approverList) ? asset.approverList : [];
  return existing.some((r) => {
    const s = String(r?.status || "").toLowerCase();
    return s.includes("return") || s.includes("rejected") || s.includes("correction");
  });
})();

const _shouldNotifyLevel1 =
  approverEnabled && (!isEditMode || _wasReturnedOrRejected);

if (_shouldNotifyLevel1) {
  const firstLevel = approverLevels[0];
  const firstRow = (computedPayload.approverList || []).find(
    (r) => Number(r.level) === firstLevel,
  );
  const firstUsers =
    Array.isArray(firstRow?.users) && firstRow.users.length > 0
      ? firstRow.users
      : (firstRow?.email ? [firstRow.email] : []);

  // Fan out in parallel so all Level-1 approvers are notified simultaneously.
  await Promise.all(
    firstUsers
      .filter(Boolean)
      .map((email) =>
        sendApprovalRequestEmail({
          approverEmail: email,
          approverName: "",
          assetId: aId,
          assetName: computedPayload.assetName,
          levelLabel: firstRow?.label || "Approver",
          level: firstLevel,
          requesterName:
            currentUser?.displayName || currentUser?.name || "",
          requesterEmail: currentUser?.email || "",
          companyName: currentUser?.company || "",
        }).catch((emailErr) => {
          console.warn(
            `[Approval Email] Level 1 notification failed for ${email}:`,
            emailErr,
          );
        }),
      ),
  );
}
        showToast(
          `Asset successfully ${isEditMode ? "updated" : "created.  Asset Id:"}` +
            aId,
          "success",
        );
        if (onSave) onSave({ ...computedPayload, id: targetDocId });

                          if (
                computedPayload.warrantyExpiry &&
                computedPayload.assetType !== "Customer"
              ) {
          try {
            const scheduled = await scheduleWarrantyReminders({
              assetId:        activeAssetId,
              assetName:      computedPayload.assetName,
              createdByEmail: currentUser.email,
              warrantyExpiry: {
                toDate: () => new Date(computedPayload.warrantyExpiry),
              },
            });

            if (scheduled.length) {
              console.log(
                `[Warranty] ${scheduled.length} reminder(s) scheduled via Brevo.`,
              );

              // Persist messageIds on the asset so we can cancel them later
              // if the user edits the warranty date or deletes the asset.
              await assetService.upsertData(
                targetDocId,
                { warrantyEmailSchedules: scheduled },
                { isCreation: false },
              );
            }
          } catch (reminderError) {
            console.warn(
              "Reminder scheduling handled safely:",
              reminderError.message,
            );
          }
        }
        onClose();
      } catch (err) {
        console.error("Submit failed:", err);
        setSubmitError(
          err.message || "Failed to save asset. Please try again.",
        );
        if (formBodyRef.current)
          formBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
      } finally {
        setLoading(false);
      }
    },
                             [
      validateForm,
      isEditMode,
      asset,
      currentUser,
      showToast,
      onSave,
      onClose,
      subCategoryData,
      ownershipEnabled,
      approverEnabled,
      approverLevels,
      approverOptionsByLevel,
      isSubmissionBlockedByApproval,
      assetApprovalLabel,
    ],
  );
  return (
    <Dialog
      fullScreen
      open={open}
      onClose={!loading ? onClose : undefined}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          width: "100vw",
          height: "100vh",
          maxWidth: "100vw",
          maxHeight: "100vh",
          margin: 0,
          boxSizing: "border-box",
        },
      }}
    >
      {/* APP BAR */}
      <AppBar
        sx={{
          position: "relative",
          flexShrink: 0,
          width: "100%",
          boxSizing: "border-box",
        }}
        elevation={1}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }} noWrap>
              {isEditMode ? "Edit Asset Record" : "Create New Asset Record"}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
              {isEditMode
                ? `Editing: ${formData.assetName || formData.assetId}`
                : "Fill in the asset details across the sections below"}
            </Typography>
          </Box>
          {isEditMode && onNavigateToAssignAsset && (
            <Button
              type="button"
              startIcon={<AssignmentIndIcon />}
              onClick={() => {
                onClose();
                onNavigateToAssignAsset(asset);
              }}
              sx={{
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.5)",
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8125rem",
                px: 1.5,
                mr: 1,
                whiteSpace: "nowrap",
                "&:hover": { background: "rgba(255,255,255,0.12)" },
              }}
            >
              Assign Asset
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* SCROLLABLE FORM BODY */}
      <Box
        component="form"
        id="asset-form"
        ref={formBodyRef}
        onSubmit={handleSubmit}
        noValidate
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          width: "100%",
          boxSizing: "border-box",
          bgcolor: "#f1f5f9",
          py: 4,
        }}
      >
             <Container maxWidth="lg">
          <Stack spacing={3}>
            {isSubmissionBlockedByApproval && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                This asset is in <strong>{assetApprovalLabel}</strong> status.
                Editing is only allowed in <strong>Draft</strong> or{" "}
                <strong>Returned</strong> status.
              </Alert>
            )}
            {/* SECTION 1 — BASIC INFORMATION */}
            <SectionCard variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <SectionTitle>Basic Information</SectionTitle>
                <FormRow>
                  <FormGroup hasError={Boolean(errors.assetName)}>
                    <FieldLabel>
                      Asset Name <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <TextField
                      id="assetName"
                      name="assetName"
                      value={formData.assetName}
                      onChange={handleChange}
                      placeholder="e.g., MacBook Pro M3"
                      fullWidth
                      size="small"
                      error={Boolean(errors.assetName)}
                      helperText={errors.assetName || ""}
                      FormHelperTextProps={{ sx: helperErrorSx }}
                    />
                  </FormGroup>

                  {
                    /* Ravi Changes */
                    isEditMode && (
                      <FormGroup>
                        <FieldLabel>Asset ID Token</FieldLabel>
                        <TextField
                          id="assetId"
                          name="assetId"
                          value={
                            assetIdStatus === "loading"
                              ? "Generating…"
                              : assetIdStatus === "error"
                                ? "Will be generated on submit"
                                : formData.assetId
                          }
                          disabled
                          fullWidth
                          size="small"
                        />
                        {assetIdStatus === "error" && (
                          <FieldHint sx={{ color: "#dc2626" }}>
                            ID generation failed — will retry on submit.
                          </FieldHint>
                        )}
                      </FormGroup>
                    )
                  }
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <FieldLabel>Asset Description</FieldLabel>
                    <TextField
                      id="assetDescription"
                      name="assetDescription"
                      value={formData.assetDescription || ""}
                      onChange={handleChange}
                      placeholder="Provide physical condition properties, specialised attributes, context details…"
                      multiline
                      rows={2}
                      maxRows={8}
                      inputProps={{ maxLength: 5000 }}
                      helperText={`${formData.assetDescription?.length || 0}/5000 characters`}
                      fullWidth
                      size="small"
                    />
                  </FormGroup>
                </FormRow>
              </CardContent>
            </SectionCard>

            {/* SECTION 2 — CLASSIFICATION & SERIAL LOGISTICS */}
            <SectionCard variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <SectionTitle>
                  Classification &amp; Serial Logistics
                </SectionTitle>

                <FormRow>
                  <FormGroup hasError={Boolean(errors.assetType)}>
                    <FieldLabel>
                      Asset Type <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <FormControl
                      fullWidth
                      size="small"
                      error={Boolean(errors.assetType)}
                    >
                                           <Select
                        id="assetType"
                        name="assetType"
                        value={formData.assetType || ""}
                        onChange={handleChange}
                        displayEmpty
                        disabled={isLoadingOptions}
                        renderValue={(selected) => {
                          if (!selected) {
                            return isLoadingOptions
                              ? "Loading types…"
                              : "— Select Asset Type —";
                          }
                          const found = assetTypeOptions.find(
                            (o) => o.value === selected,
                          );
                          return found ? found.label : selected;
                        }}
                      >
                        <MenuItem value="" disabled>
                          {isLoadingOptions
                            ? "Loading types…"
                            : "— Select Asset Type —"}
                        </MenuItem>
                        {assetTypeOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.assetType && (
                        <ErrorMessage>{errors.assetType}</ErrorMessage>
                      )}
                    </FormControl>
                    <FieldHint>
                      Choose first — determines other required fields.
                    </FieldHint>
                  </FormGroup>

                  <FormGroup hasError={Boolean(errors.ownershipType)}>
                    <FieldLabel>
                      Ownership Type <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <FormControl
                      fullWidth
                      size="small"
                      error={Boolean(errors.ownershipType)}
                    >
                      <Select
                        id="ownershipType"
                        name="ownershipType"
                        value={formData.ownershipType || ""}
                        onChange={handleChange}
                        displayEmpty
                        disabled={isLoadingOptions}
                        renderValue={(selected) => {
                          if (!selected) {
                            return isLoadingOptions
                              ? "Loading types…"
                              : "— Select Ownership Type —";
                          }
                          const found = ownershipTypeOptions.find(
                            (o) => o.value === selected,
                          );
                          return found ? found.label : selected;
                        }}
                      >
                        <MenuItem value="" disabled>
                          {isLoadingOptions
                            ? "Loading types…"
                            : "— Select Ownership Type —"}
                        </MenuItem>
                        {ownershipTypeOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.ownershipType && (
                        <ErrorMessage>{errors.ownershipType}</ErrorMessage>
                      )}
                    </FormControl>
                    <FieldHint>
                      Determines the financial section shown below.
                    </FieldHint>
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup hasError={Boolean(errors.categoryId)}>
                    <FieldLabel>
                      Category <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <FormControl
                      fullWidth
                      size="small"
                      error={Boolean(errors.categoryId)}
                      disabled={!formData.assetType || isEditMode}
                    >
                      <Select
                        id="categoryId"
                        name="categoryId"
                        value={formData.categoryId || ""}
                        onChange={handleChange}
                        displayEmpty
                      >
                        <MenuItem value="">
                          {formData.assetType
                            ? "Select Category"
                            : "— Choose Asset Type first —"}
                        </MenuItem>
                        {selectableCategories.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>
                            {cat.categoryName} ({cat.categoryId})
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.categoryId && (
                        <ErrorMessage>{errors.categoryId}</ErrorMessage>
                      )}
                    </FormControl>
                  </FormGroup>

                  <FormGroup hasError={Boolean(errors.subCategoryId)}>
                    <FieldLabel>
                      Sub-Category <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <FormControl
                      fullWidth
                      size="small"
                      error={Boolean(errors.subCategoryId)}
                      disabled={
                        !formData.categoryId ||
                        filteredSubCategories.length === 0
                      }
                    >
                      <Select
                        id="subCategoryId"
                        name="subCategoryId"
                        value={formData.subCategoryId || ""}
                        onChange={handleChange}
                        displayEmpty
                      >
                        <MenuItem value="">
                          {formData.categoryId
                            ? "Select Sub-Category"
                            : "— Choose Category first —"}
                        </MenuItem>
                        {filteredSubCategories.map((sub) => (
                          <MenuItem key={sub.id} value={sub.id}>
                            {sub.subCategoryName}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.subCategoryId && (
                        <ErrorMessage>{errors.subCategoryId}</ErrorMessage>
                      )}
                    </FormControl>
                   {subCategoryData && (
  <>
    <FieldHint sx={{ color: "#1e40af", fontStyle: "normal" }}>
      Tracking:{" "}
      <strong>
        {subCategoryData.serializationType || "Not set"}
      </strong>
    </FieldHint>
    {subCategoryData.taggingRequired === true && (
      <FieldHint
        sx={{ color: "#b45309", fontStyle: "normal", fontWeight: 600 }}
      >
         Tag verification required — Admin/Auditor will be prompted to
        photograph the tag on QR scan.
      </FieldHint>
    )}
  </>
)}
                  </FormGroup>

                  <FormGroup hasError={Boolean(errors.brandId)}>
                    <FieldLabel>
                      Brand <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <FormControl
                      fullWidth
                      size="small"
                      error={Boolean(errors.brandId)}
                      disabled={!formData.subCategoryId}
                    >
                      <Select
                        id="brandId"
                        name="brandId"
                        value={formData.brandId || ""}
                        onChange={handleChange}
                        displayEmpty
                      >
                        <MenuItem value="">
                          {formData.subCategoryId
                            ? "Select Brand"
                            : "— Choose Sub-Category first —"}
                        </MenuItem>
                        {brands.map((brand) => (
                          <MenuItem key={brand.id} value={brand.id}>
                            {brand.brandName}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.brandId && (
                        <ErrorMessage>{errors.brandId}</ErrorMessage>
                      )}
                    </FormControl>
                  </FormGroup>
                </FormRow>

                {showParentAssetField(formData.assetType) && (
                  <FormRow>
                    <FormGroup hasError={Boolean(errors.parentAssetId)}>
                      <FieldLabel>
                        Primary Asset{" "}
                        {requiresParentAsset(formData.assetType) ? (
                          <RequiredMark>*</RequiredMark>
                        ) : (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "0.75rem",
                              color: "#6b7280",
                              fontWeight: 400,
                            }}
                          >
                            (Optional)
                          </Typography>
                        )}
                      </FieldLabel>
                                         <FormControl
                        fullWidth
                        size="small"
                        error={Boolean(errors.parentAssetId)}
                        disabled={parentAssetsLoading}
                      >
                        <Select
                          id="parentAssetId"
                          name="parentAssetId"
                          value={formData.parentAssetId || ""}
                          onChange={handleChange}
                          displayEmpty
                        >
                                                                         <MenuItem value="">
                            {parentAssetsLoading
                              ? "Loading parent assets…"
                              : parentAssets.length === 0 
                                ? "— No assets found (Check Asset Types) —" 
                                : "— Select Parent Asset —"}
                          </MenuItem>
                                                   {parentAssets.map((pa) => (
                            <MenuItem key={pa.id} value={pa.id}>
                              {pa.assetId} — {pa.assetName}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.parentAssetId && (
                          <ErrorMessage>{errors.parentAssetId}</ErrorMessage>
                        )}
                      </FormControl>
                      <FieldHint>
                        Only asset types marked "Can Be Parent" are shown here.
                      </FieldHint>
                      {inheritedFromParent && (
                        <InfoBox success sx={{ mt: 1, mb: 0 }}>
                          <Typography sx={{ margin: 0, fontSize: "inherit" }}>
                            ✓ Location &amp; assignment inherited from{" "}
                            <strong>{inheritedFromParent.assetName}</strong>
                            {hasProcurementInheritance(formData.assetType) &&
                            showsFinancialSection &&
                            !formData.purchasedSeparately
                              ? " · Procurement also inherited"
                              : ""}
                          </Typography>
                        </InfoBox>
                      )}
                    </FormGroup>
                  </FormRow>
                )}

                {formData.assetType && (
  <InfoBox
    warning={
      !isReturnOnlyOwnership &&
      getAssetTypeConfig(formData.assetType).serialPolicy ===
        "sub-category" && !formData.subCategoryId
    }
  >
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {isReturnOnlyOwnership ? (
        <>
          <Typography sx={{ margin: 0, fontSize: "inherit", fontWeight: 600 }}>
            Tracking Mode: <Box component="strong">Serialized (Enforced)</Box>
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "inherit", opacity: 0.85 }}>
            {formData.ownershipType} assets are always serialized — each unit is
            tracked by its own serial number.
          </Typography>
        </>
      ) : (
        <>
          <Typography sx={{ margin: 0, fontSize: "inherit", fontWeight: 600 }}>
            Tracking Mode:{" "}
            {trackingModeLabel ? (
              <Box component="strong">{trackingModeLabel}</Box>
            ) : (
              <Box component="em">
                Select a Sub-Category to determine tracking mode
              </Box>
            )}
          </Typography>
          {trackingModeSource && (
            <Typography sx={{ fontSize: "0.75rem", color: "inherit", opacity: 0.85 }}>
              {trackingModeSource}
            </Typography>
          )}
        </>
      )}
    </Box>
  </InfoBox>
)}
                

                {!isReturnOnlyOwnership &&
  (!subCategoryData ||
    subCategoryData.serializationType !== "serialized") && (
                  <FormRow>
                    <FormGroup>
                      <FieldLabel>Tracking Mode</FieldLabel>
                      <FormControl>
                        <RadioGroup
                          row
                          value={formData.userTrackingMode}
                          onChange={(e) =>
                            handleTrackingModeChange(e.target.value)
                          }
                        >
                          <FormControlLabel
                            value="serialized"
                            disabled={
                              !subCategoryData ||
                              subCategoryData.serializationType ===
                                "non-serialized"
                            }
                            control={<Radio size="small" />}
                            label="Serialized (unique per unit)"
                          />
                          <FormControlLabel
                            value="quantity"
                            control={<Radio size="small" />}
                            label="Bulk (track by quantity)"
                          />
                        </RadioGroup>
                      </FormControl>
                      <FieldHint>
                        The Sub-Category has no tracking preference — choose
                        Serialized for items needing individualized tracking.
                      </FieldHint>
                    </FormGroup>
                  </FormRow>
                )}

                {formData.assetType &&
                  !allowSerialToggle &&
                  formData.subCategoryId &&
                  getAssetTypeConfig(formData.assetType).serialPolicy ===
                    "sub-category" && (
                    <FieldHint
                      sx={{ mb: 1.5, color: "#1e40af", fontStyle: "normal" }}
                    >
                      Tracking mode is fixed by the Sub-Category master setting
                      and cannot be changed here.
                    </FieldHint>
                  )}

                {formData.assetType && showSerialField && isSerializedAsset && (
                  <FormRow>
                    <FormGroup
                      hasError={Boolean(errors.manufacturerSerialNumber)}
                    >
                      <FieldLabel>
                        Manufacturer Serial Number
                        {serialIsRequired && <RequiredMark>*</RequiredMark>}
                      </FieldLabel>
                      <TextField
                        id="manufacturerSerialNumber"
                        name="manufacturerSerialNumber"
                        value={formData.manufacturerSerialNumber}
                        onChange={handleChange}
                        placeholder="Enter vendor/hardware serial ID"
                        fullWidth
                        size="small"
                        error={Boolean(errors.manufacturerSerialNumber)}
                        helperText={
                          errors.manufacturerSerialNumber ||
                          (serialIsRequired ? "" : "Optional")
                        }
                        FormHelperTextProps={{ sx: helperErrorSx }}
                      />
                    </FormGroup>
                  </FormRow>
                )}

                {formData.userTrackingMode !== "serialized" && (
                  <FormRow>
                    <FormGroup hasError={Boolean(errors.quantity)}>
                      <FieldLabel>
                        Quantity <RequiredMark>*</RequiredMark>
                      </FieldLabel>
                      <TextField
                        id="quantity"
                        name="quantity"
                        type="number"
                        value={formData.quantity}
                        onChange={handleChange}
                        inputProps={{ min: 1 }}
                        fullWidth
                        size="small"
                        error={Boolean(errors.quantity)}
                        helperText={errors.quantity || ""}
                        FormHelperTextProps={{ sx: helperErrorSx }}
                      />
                    </FormGroup>

                   
                                          <FormGroup>
                      <FieldLabel>Unit of Measurement</FieldLabel>
                      <FormControl
                        fullWidth
                        size="small"
                        disabled={isLoadingOptions}
                      >
                        <Select
                          id="unitOfMeasurement"
                          name="unitOfMeasurement"
                          value={formData.unitOfMeasurement || ""}
                          onChange={handleChange}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return isLoadingOptions
                                ? "Loading…"
                                : "Select Unit";
                            }
                            const found = unitOfMeasurementOptions.find(
                              (o) => o.value === selected,
                            );
                            return found ? found.label : selected;
                          }}
                        >
                          <MenuItem value="">
                            {isLoadingOptions ? "Loading…" : "Select Unit"}
                          </MenuItem>
                          {unitOfMeasurementOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                    
                      </FormControl>
                    </FormGroup>

                    {currentHasReorderLevel && (
                      <FormGroup>
                        <FieldLabel>Reorder Level</FieldLabel>
                        <TextField
                          id="reorderLevel"
                          name="reorderLevel"
                          type="number"
                          value={formData.reorderLevel || ""}
                          onChange={handleChange}
                          inputProps={{ min: 0 }}
                          fullWidth
                          size="small"
                          helperText="Alert when stock falls below this quantity"
                        />
                      </FormGroup>
                    )}
                  </FormRow>
                )}

                {formData.assetType && currentHasExpiryDate && (
                  <FormRow>
                    <FormGroup hasError={Boolean(errors.expiryDate)}>
                      <FieldLabel>
                        Expiry Date <RequiredMark>*</RequiredMark>
                      </FieldLabel>
                      <TextField
                        id="expiryDate"
                        name="expiryDate"
                        type="date"
                        value={formData.expiryDate || ""}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(errors.expiryDate)}
                        helperText={
                          errors.expiryDate ||
                          "Required for perishables (food, chemicals, medical)"
                        }
                        FormHelperTextProps={{
                          sx: errors.expiryDate ? helperErrorSx : {},
                        }}
                      />
                    </FormGroup>
                  </FormRow>
                )}
              </CardContent>
            </SectionCard>

            {/* SECTION 3 — FINANCIAL & PROCUREMENT */}
            {(showsFinancialSection ||
              showsLeaseFields ||
              showsRentalFields) && (
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>
                    Financial &amp; Procurement Data
                    {showsLeaseFields && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 400,
                          color: "#2563eb",
                          ml: 1,
                        }}
                      >
                        — Lease Information
                      </Typography>
                    )}
                    {showsRentalFields && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 400,
                          color: "#7c3aed",
                          ml: 1,
                        }}
                      >
                        — Rental Information
                      </Typography>
                    )}
                  </SectionTitle>

                  {showsFinancialSection && (
                    <>
                      {showPurchasedSeparatelyUI && (
                        <InfoBox sx={{ mb: 2 }}>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: "inherit",
                                mb: 0.25,
                              }}
                            >
                              Purchased Separately?
                            </Typography>
                            <Typography sx={{ fontSize: "0.75rem" }}>
                              {formData.purchasedSeparately
                                ? "Enter independent procurement details below."
                                : "Procurement details are inherited from the Primary Asset (read-only)."}
                            </Typography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.purchasedSeparately}
                                onChange={(e) =>
                                  handlePurchasedSeparatelyChange(
                                    e.target.checked,
                                  )
                                }
                                size="small"
                                sx={{ color: "#1e40af" }}
                              />
                            }
                            label={
                              <Typography
                                sx={{
                                  fontSize: "0.8125rem",
                                  fontWeight: 600,
                                  color: "#1e40af",
                                }}
                              >
                                Purchased Separately
                              </Typography>
                            }
                          />
                        </InfoBox>
                      )}

                      <FormRow>
                        <FormGroup hasError={Boolean(errors.purchaseDate)}>
                          <FieldLabel>
                            Purchase Date <RequiredMark>*</RequiredMark>
                          </FieldLabel>
                          <TextField
                            id="purchaseDate"
                            name="purchaseDate"
                            type="date"
                            value={formData.purchaseDate}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            disabled={isProcurementInherited}
                            sx={isProcurementInherited ? inheritedFieldSx : {}}
                            error={Boolean(errors.purchaseDate)}
                            helperText={errors.purchaseDate || ""}
                            FormHelperTextProps={{ sx: helperErrorSx }}
                            InputLabelProps={{ shrink: true }}
                          />
                          {isProcurementInherited && (
                            <InheritedNote
                              parentName={inheritedFromParent?.assetName}
                            />
                          )}
                        </FormGroup>

                        <FormGroup hasError={Boolean(errors.purchaseValue)}>
                          <FieldLabel>
                            Purchase Value (INR) <RequiredMark>*</RequiredMark>
                          </FieldLabel>
                          <TextField
                            id="purchaseValue"
                            name="purchaseValue"
                            type="number"
                            value={formData.purchaseValue}
                            onChange={handleChange}
                            inputProps={{ min: 0, step: "0.01" }}
                            fullWidth
                            size="small"
                            disabled={isProcurementInherited}
                            sx={isProcurementInherited ? inheritedFieldSx : {}}
                            error={Boolean(errors.purchaseValue)}
                            helperText={errors.purchaseValue || ""}
                            FormHelperTextProps={{ sx: helperErrorSx }}
                          />
                          {isProcurementInherited && (
                            <InheritedNote
                              parentName={inheritedFromParent?.assetName}
                            />
                          )}
                        </FormGroup>

                        <FormGroup hasError={Boolean(errors.vendorId)}>
                          <FieldLabel>
                            Primary Vendor <RequiredMark>*</RequiredMark>
                          </FieldLabel>
                          <FormControl
                            fullWidth
                            size="small"
                            error={Boolean(errors.vendorId)}
                            disabled={isProcurementInherited}
                          >
                            <Select
                              id="vendorId"
                              name="vendorId"
                              value={formData.vendorId || ""}
                              onChange={handleChange}
                              displayEmpty
                              sx={
                                isProcurementInherited
                                  ? { bgcolor: "#f3f4f6" }
                                  : {}
                              }
                            >
                              <MenuItem value="">Select Vendor</MenuItem>
                              {vendors.map((v) => (
                                <MenuItem key={v.id} value={v.id}>
                                  {v.vendorName}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.vendorId && (
                              <ErrorMessage>{errors.vendorId}</ErrorMessage>
                            )}
                          </FormControl>
                          {isProcurementInherited && (
                            <InheritedNote
                              parentName={inheritedFromParent?.assetName}
                            />
                          )}
                        </FormGroup>
                      </FormRow>

                                        <FormRow>
                        {formData.assetType !== "Customer" && (
                          <FormGroup>
                            <FieldLabel>Warranty Expiry</FieldLabel>
                            <TextField
                              id="warrantyExpiry"
                              name="warrantyExpiry"
                              type="date"
                              value={formData.warrantyExpiry}
                              onChange={handleChange}
                              fullWidth
                              size="small"
                              disabled={isProcurementInherited}
                              sx={isProcurementInherited ? inheritedFieldSx : {}}
                              InputLabelProps={{ shrink: true }}
                            />
                            {isProcurementInherited && (
                              <InheritedNote
                                parentName={inheritedFromParent?.assetName}
                              />
                            )}
                          </FormGroup>
                        )}

                        <FormGroup>
                          <FieldLabel>Invoice Identifier Token</FieldLabel>
                          <TextField
                            id="invoiceNumber"
                            name="invoiceNumber"
                            value={formData.invoiceNumber}
                            onChange={handleChange}
                            placeholder="Enter invoice / PO reference"
                            fullWidth
                            size="small"
                            disabled={isProcurementInherited}
                            sx={isProcurementInherited ? inheritedFieldSx : {}}
                          />
                          {isProcurementInherited && (
                            <InheritedNote
                              parentName={inheritedFromParent?.assetName}
                            />
                          )}
                        </FormGroup>

                        {!isProcurementInherited ? (
                          <FormGroup>
                            <FieldLabel>Upload Invoice</FieldLabel>
                            <input
                              ref={fileInputRef}
                              id="invoiceFile"
                              name="invoiceFile"
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png,.webp"
                              onChange={handleFileInputChange}
                              style={{ display: "none" }}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                              }}
                            >
                              <Button
                                type="button"
                                variant="outlined"
                                size="small"
                                onClick={handleChooseFileClick}
                                sx={{
                                  flex: 1,
                                  textTransform: "none",
                                  fontWeight: 500,
                                  fontSize: "0.8125rem",
                                  borderStyle: "dashed",
                                  borderColor: "#d1d5db",
                                  color: "#374151",
                                  bgcolor: "#f9fafb",
                                  py: "0.45rem",
                                  "&:hover": {
                                    bgcolor: "#f3f4f6",
                                    borderColor: "#9ca3af",
                                  },
                                }}
                              >
                                {formData.invoiceFileName
                                  ? "Change File"
                                  : "Choose File"}
                              </Button>
                              {formData.invoiceFileName && (
                                <Button
                                  type="button"
                                  onClick={handleClearFile}
                                  sx={{
                                    minWidth: "auto",
                                    background: "none",
                                    border: "1px solid #fca5a5",
                                    color: "#dc2626",
                                    borderRadius: "4px",
                                    fontSize: "1.25rem",
                                    padding: "0.25rem 0.5rem",
                                    lineHeight: 1,
                                    "&:hover": { background: "#fef2f2" },
                                  }}
                                >
                                  ×
                                </Button>
                              )}
                            </Box>
                            {formData.invoiceFileName && (
                              <Typography
                                sx={{
                                  display: "block",
                                  mt: "0.25rem",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#374151",
                                  wordBreak: "break-all",
                                }}
                              >
                                Selected: {formData.invoiceFileName}
                              </Typography>
                            )}
                            {isEditMode &&
                              formData.invoiceFileUrl &&
                              !formData.invoiceFileName && (
                                <Typography
                                  sx={{
                                    display: "block",
                                    mt: "0.25rem",
                                    fontSize: "0.75rem",
                                    color: "#2563eb",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  Current:{" "}
                                  {formData.invoiceFileName ||
                                    "Uploaded invoice"}
                                </Typography>
                              )}
                            <FieldHint sx={{ mt: "0.25rem" }}>
                              Max 750 KB · PNG, JPG, WEBP, PDF only.
                            </FieldHint>
                          </FormGroup>
                        ) : (
                          inheritedFromParent?.invoiceFileName && (
                            <FormGroup>
                              <FieldLabel>Invoice</FieldLabel>
                              <Box
                                sx={{
                                  p: 1,
                                  bgcolor: "#f3f4f6",
                                  borderRadius: 1,
                                  border: "1px solid #e5e7eb",
                                  fontSize: "0.8125rem",
                                  color: "#374151",
                                }}
                              >
                                📎 {inheritedFromParent.invoiceFileName}
                              </Box>
                              <InheritedNote
                                parentName={inheritedFromParent?.assetName}
                              />
                            </FormGroup>
                          )
                        )}
                      </FormRow>
                    </>
                  )}

                  {/* LEASED FIELDS */}
                  {showsLeaseFields && (
                    <>
                      <InfoBox sx={{ mb: 2 }}>
                        <Typography sx={{ margin: 0, fontSize: "inherit" }}>
                          This asset is <strong>Leased</strong>. Enter lease
                          agreement details. Purchase information is not
                          applicable.
                        </Typography>
                      </InfoBox>
                      <FormRow>
                        <FormGroup hasError={Boolean(errors.leaseStartDate)}>
                          <FieldLabel>
                            Lease Start Date <RequiredMark>*</RequiredMark>
                          </FieldLabel>
                          <TextField
                            id="leaseStartDate"
                            name="leaseStartDate"
                            type="date"
                            value={formData.leaseStartDate}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            error={Boolean(errors.leaseStartDate)}
                            helperText={errors.leaseStartDate || ""}
                            FormHelperTextProps={{ sx: helperErrorSx }}
                          />
                        </FormGroup>
                        <FormGroup hasError={Boolean(errors.leaseEndDate)}>
                          <FieldLabel>
                            Lease End Date <RequiredMark>*</RequiredMark>
                          </FieldLabel>
                          <TextField
                            id="leaseEndDate"
                            name="leaseEndDate"
                            type="date"
                            value={formData.leaseEndDate}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              min: formData.leaseStartDate || undefined,
                            }}
                            error={Boolean(errors.leaseEndDate)}
                            helperText={errors.leaseEndDate || ""}
                            FormHelperTextProps={{ sx: helperErrorSx }}
                          />
                        </FormGroup>
                        <FormGroup hasError={Boolean(errors.leaseAmount)}>
                          <FieldLabel>
                            Lease Amount (INR) <RequiredMark>*</RequiredMark>
                          </FieldLabel>
                          <TextField
                            id="leaseAmount"
                            name="leaseAmount"
                            type="number"
                            value={formData.leaseAmount}
                            onChange={handleChange}
                            inputProps={{ min: 0, step: "0.01" }}
                            fullWidth
                            size="small"
                            error={Boolean(errors.leaseAmount)}
                            helperText={errors.leaseAmount || ""}
                            FormHelperTextProps={{ sx: helperErrorSx }}
                          />
                        </FormGroup>
                      </FormRow>
                      <FormRow>
                        <FormGroup hasError={Boolean(errors.leaseVendorId)}>
                          <FieldLabel>
                            Leasing Company <RequiredMark>*</RequiredMark>
                          </FieldLabel>
                          <FormControl
                            fullWidth
                            size="small"
                            error={Boolean(errors.leaseVendorId)}
                          >
                            <Select
                              id="leaseVendorId"
                              name="leaseVendorId"
                              value={formData.leaseVendorId || ""}
                              onChange={handleChange}
                              displayEmpty
                            >
                              <MenuItem value="">Select Vendor</MenuItem>
                              {vendors.map((v) => (
                                <MenuItem key={v.id} value={v.id}>
                                  {v.vendorName}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.leaseVendorId && (
                              <ErrorMessage>
                                {errors.leaseVendorId}
                              </ErrorMessage>
                            )}
                          </FormControl>
                        </FormGroup>
                        <FormGroup
                          hasError={Boolean(errors.leaseAgreementNumber)}
                        >
                          <FieldLabel>
                            Lease Agreement Number{" "}
                            <RequiredMark>*</RequiredMark>
                          </FieldLabel>
                          <TextField
                            id="leaseAgreementNumber"
                            name="leaseAgreementNumber"
                            value={formData.leaseAgreementNumber}
                            onChange={handleChange}
                            placeholder="e.g. LSE-2024-001"
                            fullWidth
                            size="small"
                            error={Boolean(errors.leaseAgreementNumber)}
                            helperText={errors.leaseAgreementNumber || ""}
                            FormHelperTextProps={{ sx: helperErrorSx }}
                          />
                        </FormGroup>
                        {requiresBillingFrequency && (
                          <FormGroup
                            hasError={Boolean(errors.leaseBillingFrequency)}
                          >
                            <FieldLabel>
                              Billing Frequency <RequiredMark>*</RequiredMark>
                            </FieldLabel>
                            <FormControl
                              fullWidth
                              size="small"
                              error={Boolean(errors.leaseBillingFrequency)}
                            >
                                 <Select
  id="leaseBillingFrequency"
  name="leaseBillingFrequency"
  value={formData.leaseBillingFrequency || ""}
  onChange={handleChange}
  displayEmpty
  disabled={isLoadingOptions}
  renderValue={(selected) => {
    if (!selected) {
      return isLoadingOptions ? "Loading…" : "Select Frequency";
    }
    const found = billingFrequencyOptions.find(
      (o) => o.value === selected
    );
    return found ? found.label : selected;
  }}
>
  <MenuItem value="">
    {isLoadingOptions ? "Loading…" : "Select Frequency"}
  </MenuItem>
  {billingFrequencyOptions.map((opt) => (
    <MenuItem key={opt.value} value={opt.value}>
      {opt.label}
    </MenuItem>
  ))}
</Select>
                              {errors.leaseBillingFrequency && (
                                <ErrorMessage>
                                  {errors.leaseBillingFrequency}
                                </ErrorMessage>
                              )}
                            </FormControl>
                          </FormGroup>
                        )}
                      </FormRow>
                    </>
                  )}

                  {/* RENTED FIELDS */}
                  {showsRentalFields && (
                    <>
                      <InfoBox
                        sx={{
                          mb: 2,
                          background: "#faf5ff",
                          borderColor: "#e9d5ff",
                          color: "#6b21a8",
                        }}
                      >
                        <Typography sx={{ margin: 0, fontSize: "inherit" }}>
                          This asset is <strong>Rented</strong>. Enter rental
                          agreement details. Purchase information is not
                          applicable.
                        </Typography>
                      </InfoBox>
                      <FormRow>
                        <FormGroup hasError={Boolean(errors.rentStartDate)}>
                          <FieldLabel>
                            Rent Start Date <RequiredMark>*</RequiredMark>
                          </FieldLabel>
                          <TextField
                            id="rentStartDate"
                            name="rentStartDate"
                            type="date"
                            value={formData.rentStartDate}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            error={Boolean(errors.rentStartDate)}
                            helperText={errors.rentStartDate || ""}
                            FormHelperTextProps={{ sx: helperErrorSx }}
                          />
                        </FormGroup>
                        <FormGroup
                          hasError={Boolean(errors.expectedReturnDate)}
                        >
                          <FieldLabel>
                            Expected Return Date <RequiredMark>*</RequiredMark>
                          </FieldLabel>
                          <TextField
                            id="expectedReturnDate"
                            name="expectedReturnDate"
                            type="date"
                            value={formData.expectedReturnDate}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              min: formData.rentStartDate || undefined,
                            }}
                            error={Boolean(errors.expectedReturnDate)}
                            helperText={errors.expectedReturnDate || ""}
                            FormHelperTextProps={{ sx: helperErrorSx }}
                          />
                        </FormGroup>
                        <FormGroup hasError={Boolean(errors.rentAmount)}>
                          <FieldLabel>
                            Rent Amount (INR) <RequiredMark>*</RequiredMark>
                          </FieldLabel>
                          <TextField
                            id="rentAmount"
                            name="rentAmount"
                            type="number"
                            value={formData.rentAmount}
                            onChange={handleChange}
                            inputProps={{ min: 0, step: "0.01" }}
                            fullWidth
                            size="small"
                            error={Boolean(errors.rentAmount)}
                            helperText={errors.rentAmount || ""}
                            FormHelperTextProps={{ sx: helperErrorSx }}
                          />
                        </FormGroup>
                      </FormRow>
                      <FormRow>
                        <FormGroup hasError={Boolean(errors.rentVendorId)}>
                          <FieldLabel>
                            Rental Company <RequiredMark>*</RequiredMark>
                          </FieldLabel>
                          <FormControl
                            fullWidth
                            size="small"
                            error={Boolean(errors.rentVendorId)}
                          >
                            <Select
                              id="rentVendorId"
                              name="rentVendorId"
                              value={formData.rentVendorId || ""}
                              onChange={handleChange}
                              displayEmpty
                            >
                              <MenuItem value="">Select Vendor</MenuItem>
                              {vendors.map((v) => (
                                <MenuItem key={v.id} value={v.id}>
                                  {v.vendorName}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.rentVendorId && (
                              <ErrorMessage>{errors.rentVendorId}</ErrorMessage>
                            )}
                          </FormControl>
                        </FormGroup>
                        <FormGroup
                          hasError={Boolean(errors.rentAgreementNumber)}
                        >
                          <FieldLabel>
                            Rent Agreement Number <RequiredMark>*</RequiredMark>
                          </FieldLabel>
                          <TextField
                            id="rentAgreementNumber"
                            name="rentAgreementNumber"
                            value={formData.rentAgreementNumber}
                            onChange={handleChange}
                            placeholder="e.g. RNT-2024-001"
                            fullWidth
                            size="small"
                            error={Boolean(errors.rentAgreementNumber)}
                            helperText={errors.rentAgreementNumber || ""}
                            FormHelperTextProps={{ sx: helperErrorSx }}
                          />
                        </FormGroup>
                        {requiresBillingFrequency && (
                          <FormGroup
                            hasError={Boolean(errors.rentBillingFrequency)}
                          >
                            <FieldLabel>
                              Billing Frequency <RequiredMark>*</RequiredMark>
                            </FieldLabel>
                            <FormControl
                              fullWidth
                              size="small"
                              error={Boolean(errors.rentBillingFrequency)}
                            >
                                                        <Select
  id="rentBillingFrequency"
  name="rentBillingFrequency"
  value={formData.rentBillingFrequency || ""}
  onChange={handleChange}
  displayEmpty
  disabled={isLoadingOptions}
  renderValue={(selected) => {
    if (!selected) {
      return isLoadingOptions ? "Loading…" : "Select Frequency";
    }
    const found = billingFrequencyOptions.find(
      (o) => o.value === selected
    );
    return found ? found.label : selected;
  }}
>
  <MenuItem value="">
    {isLoadingOptions ? "Loading…" : "Select Frequency"}
  </MenuItem>
  {billingFrequencyOptions.map((opt) => (
    <MenuItem key={opt.value} value={opt.value}>
      {opt.label}
    </MenuItem>
  ))}
</Select>
                            
                              {errors.rentBillingFrequency && (
                                <ErrorMessage>
                                  {errors.rentBillingFrequency}
                                </ErrorMessage>
                              )}
                            </FormControl>
                          </FormGroup>
                        )}
                      </FormRow>
                    </>
                  )}
                </CardContent>
              </SectionCard>
                        )}

            {/* SECTION 3.5 — SALES & WARRANTY DETAILS (CUSTOMER ASSET ONLY) */}
            {formData.assetType === "Customer" && (
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>Sales &amp; Warranty Details</SectionTitle>
                  <FormRow>
                    <FormGroup hasError={Boolean(errors.salesDate)}>
                      <FieldLabel>Sales Date <RequiredMark>*</RequiredMark></FieldLabel>
                      <TextField
                        id="salesDate"
                        name="salesDate"
                        type="date"
                        value={formData.salesDate}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(errors.salesDate)}
                        helperText={errors.salesDate || ""}
                        FormHelperTextProps={{ sx: helperErrorSx }}
                      />
                    </FormGroup>

                    <FormGroup hasError={Boolean(errors.salesValue)}>
                      <FieldLabel>Sales Value (INR) <RequiredMark>*</RequiredMark></FieldLabel>
                      <TextField
                        id="salesValue"
                        name="salesValue"
                        type="number"
                        value={formData.salesValue}
                        onChange={handleChange}
                        inputProps={{ min: 0, step: "0.01" }}
                        fullWidth
                        size="small"
                        error={Boolean(errors.salesValue)}
                        helperText={errors.salesValue || ""}
                        FormHelperTextProps={{ sx: helperErrorSx }}
                      />
                    </FormGroup>

                    <FormGroup hasError={Boolean(errors.customerId)}>
                      <FieldLabel>Primary Customer <RequiredMark>*</RequiredMark></FieldLabel>
                      <FormControl fullWidth size="small" error={Boolean(errors.customerId)}>
                        <Select
                          id="customerId"
                          name="customerId"
                          value={formData.customerId || ""}
                          onChange={handleChange}
                          displayEmpty
                        >
                          <MenuItem value="">Select Customer</MenuItem>
                          {customers.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.customerName} ({c.customerId})
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.customerId && <ErrorMessage>{errors.customerId}</ErrorMessage>}
                      </FormControl>
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup hasError={Boolean(errors.warrantyExpiry)}>
                      <FieldLabel>Warranty Expiry Date</FieldLabel>
                      <TextField
                        id="warrantyExpiry"
                        name="warrantyExpiry"
                        type="date"
                        value={formData.warrantyExpiry}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(errors.warrantyExpiry)}
                        helperText={errors.warrantyExpiry || ""}
                        FormHelperTextProps={{ sx: helperErrorSx }}
                      />
                    </FormGroup>

                    <FormGroup>
                      <FieldLabel>Sales Invoice Number</FieldLabel>
                      <TextField
                        id="salesInvoiceNumber"
                        name="salesInvoiceNumber"
                        value={formData.salesInvoiceNumber}
                        onChange={handleChange}
                        placeholder="Enter sales invoice ref"
                        fullWidth
                        size="small"
                      />
                    </FormGroup>

                                       <FormGroup>
                      <FieldLabel>Upload Sales Invoice</FieldLabel>
                      <input
                        ref={salesFileInputRef}
                        id="salesInvoiceFile"
                        name="salesInvoiceFile"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        onChange={handleSalesFileInputChange}
                        style={{ display: "none" }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <Button
                          type="button"
                          variant="outlined"
                          size="small"
                          onClick={handleChooseSalesFileClick}
                          sx={{
                            flex: 1,
                            textTransform: "none",
                            fontWeight: 500,
                            fontSize: "0.8125rem",
                            borderStyle: "dashed",
                            borderColor: "#d1d5db",
                            color: "#374151",
                            bgcolor: "#f9fafb",
                            py: "0.45rem",
                            "&:hover": {
                              bgcolor: "#f3f4f6",
                              borderColor: "#9ca3af",
                            },
                          }}
                        >
                          {formData.salesInvoiceFileName
                            ? "Change File"
                            : "Choose File"}
                        </Button>
                        {formData.salesInvoiceFileName && (
                          <Button
                            type="button"
                            onClick={handleClearSalesFile}
                            sx={{
                              minWidth: "auto",
                              background: "none",
                              border: "1px solid #fca5a5",
                              color: "#dc2626",
                              borderRadius: "4px",
                              fontSize: "1.25rem",
                              padding: "0.25rem 0.5rem",
                              lineHeight: 1,
                              "&:hover": { background: "#fef2f2" },
                            }}
                          >
                            ×
                          </Button>
                        )}
                      </Box>
                      {formData.salesInvoiceFileName && (
                        <Typography
                          sx={{
                            display: "block",
                            mt: "0.25rem",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            color: "#374151",
                            wordBreak: "break-all",
                          }}
                        >
                          Selected: {formData.salesInvoiceFileName}
                        </Typography>
                      )}
                      {isEditMode &&
                        formData.salesInvoiceFileUrl &&
                        !formData.salesInvoiceFileName && (
                          <Typography
                            sx={{
                              display: "block",
                              mt: "0.25rem",
                              fontSize: "0.75rem",
                              color: "#2563eb",
                              wordBreak: "break-all",
                            }}
                          >
                            Current:{" "}
                            {formData.salesInvoiceFileName ||
                              "Uploaded sales invoice"}
                          </Typography>
                        )}
                      <FieldHint sx={{ mt: "0.25rem" }}>
                        Max 750 KB · PNG, JPG, WEBP, PDF only.
                      </FieldHint>
                    </FormGroup>
                  </FormRow>
                </CardContent>
              </SectionCard>
            )}

            {/* SECTION 4 — DEPRECIATION */}
            {showsDepreciation && (
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ mb: 2.5, pb: 1.5, borderBottom: "1px solid #e5e7eb" }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ color: "#1e293b", fontSize: "0.9375rem" }}
                    >
                      Depreciation Details
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 400,
                        color: "#6b7280",
                        fontStyle: "italic",
                      }}
                    >
                      (Optional)
                    </Typography>
                  </Box>

                  {formData.subCategoryId && subCategoryData && (
                    <InfoBox>
                      <Typography sx={{ margin: 0, fontSize: "inherit" }}>
                        {formData.isDepreciationAuto
                          ? `Depreciation settings loaded from sub-category: ${subCategoryData.subCategoryName}. You can edit below to override.`
                          : "Using custom depreciation settings for this asset."}
                      </Typography>
                      {!formData.isDepreciationAuto && (
                        <LinkButton
                          type="button"
                          onClick={handleResetToDefault}
                        >
                          Reset to Sub-Category
                        </LinkButton>
                      )}
                    </InfoBox>
                  )}

                  <FormRow>
                    <FormGroup hasError={Boolean(errors.depreciationMethod)}>
                      <FieldLabel>Depreciation Method</FieldLabel>
                      <FormControl
                        fullWidth
                        size="small"
                        error={Boolean(errors.depreciationMethod)}
                      >
                        <Select
  id="depreciationMethod"
  name="depreciationMethod"
  value={formData.depreciationMethod || ""}
  onChange={handleDepreciationChange}
  displayEmpty
  disabled={isLoadingOptions}
  renderValue={(selected) => {
    if (!selected) {
      return isLoadingOptions ? "Loading…" : "— Not Set —";
    }
    
    // Force upper case for robust matching (SLM, WDV, NONE)
    const upperSelected = String(selected).toUpperCase();
    
    const found = depreciationMethodOptions.find(
      (o) => String(o.value).toUpperCase() === upperSelected
    );
    
    if (!found && process.env.NODE_ENV === "development") {
      console.warn(
        `[DepreciationMethod] Unknown value "${selected}" not in SVL.`,
        "Available:", depreciationMethodOptions.map((o) => o.value)
      );
    }
    return found ? found.label : `${selected}`;
  }}
>
  <MenuItem value="">
    {isLoadingOptions ? "Loading…" : "— Not Set —"}
  </MenuItem>
  {depreciationMethodOptions.map((opt) => (
    <MenuItem key={opt.value} value={opt.value}>
      {opt.label}
    </MenuItem>
  ))}
</Select>
                        {errors.depreciationMethod && (
                          <ErrorMessage>
                            {errors.depreciationMethod}
                          </ErrorMessage>
                        )}
                      </FormControl>
                      <FieldHint>
                        Leave as None to skip depreciation tracking for this
                        asset.
                      </FieldHint>
                    </FormGroup>

                    <FormGroup hasError={Boolean(errors.residualValue)}>
                      <FieldLabel>
                        Residual Value (₹)
                        {formData.depreciationMethod ===
                          DEPRECIATION_METHODS.SLM && (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "0.7rem",
                              color: "#6b7280",
                              fontWeight: 400,
                              ml: "0.25rem",
                              textTransform: "none",
                            }}
                          >
                            (optional, defaults to ₹0)
                          </Typography>
                        )}
                      </FieldLabel>
                      <TextField
                        id="residualValue"
                        name="residualValue"
                        type="number"
                        value={formData.residualValue}
                        onChange={handleChange}
                        disabled={
                          isResidualValueReadOnly ||
                          !formData.depreciationMethod
                        }
                        inputProps={{
                          min: 0,
                          step: "0.01",
                          readOnly: isResidualValueReadOnly,
                        }}
                        placeholder={
                          isResidualValueReadOnly
                            ? "Not applicable for WDV"
                            : !formData.depreciationMethod
                              ? "Select a depreciation method first"
                              : "e.g. 10000"
                        }
                        fullWidth
                        size="small"
                        error={Boolean(errors.residualValue)}
                        helperText={
                          isResidualValueReadOnly
                            ? "Residual Value is not applicable for WDV (fixed at ₹0)"
                            : errors.residualValue ||
                              "Optional salvage value — defaults to ₹0 if left blank."
                        }
                        FormHelperTextProps={{
                          sx: {
                            color: errors.residualValue ? "#dc2626" : "#6b7280",
                            fontSize: "0.75rem",
                            fontWeight: isResidualValueReadOnly ? 400 : 500,
                            margin: 0,
                            marginTop: "4px",
                            fontStyle: isResidualValueReadOnly
                              ? "italic"
                              : "normal",
                          },
                        }}
                        sx={residualFieldSx}
                      />
                    </FormGroup>
                  </FormRow>

                  {formData.depreciationMethod === DEPRECIATION_METHODS.SLM && (
                    <FormRow>
                      <FormGroup hasError={Boolean(errors.usefulLife)}>
                        <FieldLabel>
                          Useful Life (Years) <RequiredMark>*</RequiredMark>
                        </FieldLabel>
                        <TextField
                          id="usefulLife"
                          name="usefulLife"
                          type="number"
                          value={formData.usefulLife}
                          onChange={handleDepreciationChange}
                          inputProps={{ min: 1, max: 50 }}
                          fullWidth
                          size="small"
                          error={Boolean(errors.usefulLife)}
                          helperText={errors.usefulLife || ""}
                          FormHelperTextProps={{ sx: helperErrorSx }}
                        />
                        <FieldHint>
                          {subCategoryData?.usefulLife &&
                          formData.isDepreciationAuto
                            ? `Default from sub-category: ${subCategoryData.usefulLife} years`
                            : "Enter useful life in years (1–50)"}
                        </FieldHint>
                      </FormGroup>
                    </FormRow>
                  )}

                  {formData.depreciationMethod === DEPRECIATION_METHODS.WDV && (
                    <FormRow>
                      <FormGroup hasError={Boolean(errors.depreciationRate)}>
                        <FieldLabel>
                          Depreciation Rate (%) <RequiredMark>*</RequiredMark>
                        </FieldLabel>
                        <TextField
                          id="depreciationRate"
                          name="depreciationRate"
                          type="number"
                          value={formData.depreciationRate}
                          onChange={handleDepreciationChange}
                          inputProps={{ min: 0, max: 100, step: "0.1" }}
                          fullWidth
                          size="small"
                          error={Boolean(errors.depreciationRate)}
                          helperText={errors.depreciationRate || ""}
                          FormHelperTextProps={{ sx: helperErrorSx }}
                        />
                        <FieldHint>
                          {subCategoryData?.depreciationRate &&
                          formData.isDepreciationAuto
                            ? `Default from sub-category: ${subCategoryData.depreciationRate}%`
                            : "Enter depreciation rate percentage (0–100)"}
                        </FieldHint>
                      </FormGroup>

                      <FormGroup hasError={Boolean(errors.openingBookValue)}>
                        <FieldLabel>
                          Opening Book Value (₹)
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "0.7rem",
                              color: "#6b7280",
                              fontWeight: 400,
                              ml: "0.25rem",
                              textTransform: "none",
                            }}
                          >
                            (optional, defaults to Purchase Value)
                          </Typography>
                        </FieldLabel>
                        <TextField
                          id="openingBookValue"
                          name="openingBookValue"
                          type="number"
                          value={formData.openingBookValue}
                          onChange={handleOpeningBookValueChange}
                          inputProps={{ min: 0, step: "0.01" }}
                          placeholder="e.g. 80000"
                          fullWidth
                          size="small"
                          error={Boolean(errors.openingBookValue)}
                          helperText={
                            errors.openingBookValue ||
                            "Net Book Value at the start of depreciation tracking."
                          }
                          FormHelperTextProps={{
                            sx: {
                              color: errors.openingBookValue
                                ? "#dc2626"
                                : "#6b7280",
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              margin: 0,
                              marginTop: "4px",
                            },
                          }}
                        />
                        {formData.isOpeningBookValueManual && (
                          <LinkButton
                            type="button"
                            onClick={handleResetOpeningBookValue}
                            sx={{ mt: "0.25rem", alignSelf: "flex-start" }}
                          >
                            Reset to Purchase Value
                          </LinkButton>
                        )}
                      </FormGroup>
                    </FormRow>
                  )}

                  {formData.depreciationMethod && (
                    <FormRow sx={calcRowSx}>
                      <FormGroup>
                        <FieldLabel sx={{ fontWeight: 700, color: "#065f46" }}>
                          Annual Depreciation (Auto-calculated)
                        </FieldLabel>
                        <TextField
                          value={
                            depreciationPreview
                              ? formatCurrency(
                                  depreciationPreview.annualDepreciation,
                                )
                              : "— Enter required fields above —"
                          }
                          InputProps={{ readOnly: true }}
                          disabled
                          fullWidth
                          size="small"
                          sx={annualFieldSx}
                        />
                        <FieldHint sx={{ color: "#065f46" }}>
                          {depreciationPreview?.method === "SLM"
                            ? depreciationPreview.formula
                            : depreciationPreview?.method === "WDV"
                              ? `WDV Year 1: ${formatCurrency(depreciationPreview.openingBookValue)} × ${depreciationPreview.rate}%`
                              : "Complete the fields above to see the calculation."}
                        </FieldHint>
                      </FormGroup>
                      <FormGroup>
                        <FieldLabel sx={{ fontWeight: 600 }}>
                          Monthly Depreciation
                        </FieldLabel>
                        <TextField
                          value={
                            depreciationPreview
                              ? formatCurrency(
                                  depreciationPreview.monthlyDepreciation,
                                )
                              : "₹0"
                          }
                          InputProps={{ readOnly: true }}
                          disabled
                          fullWidth
                          size="small"
                          sx={readOnlyFieldSx}
                        />
                        <FieldHint>Annual ÷ 12</FieldHint>
                      </FormGroup>
                      <FormGroup>
                        <FieldLabel sx={{ fontWeight: 600 }}>
                          Current Book Value (As of Today)
                        </FieldLabel>
                        <TextField
                          value={
                            depreciationPreview
                              ? formatCurrency(
                                  depreciationPreview.currentBookValue,
                                )
                              : formatCurrency(toNumber(formData.purchaseValue))
                          }
                          InputProps={{ readOnly: true }}
                          disabled
                          fullWidth
                          size="small"
                          sx={readOnlyFieldSx}
                        />
                        <FieldHint>
                          {depreciationPreview &&
                          depreciationPreview.yearsElapsed > 0
                            ? `After ${depreciationPreview.yearsElapsed} yrs elapsed`
                            : "Enter purchase date to compute"}
                        </FieldHint>
                      </FormGroup>
                    </FormRow>
                  )}

                  {depreciationPreview && formData.purchaseValue && (
                    <PreviewBox>
                      <Typography
                        sx={{
                          margin: "0 0 1rem 0",
                          fontSize: "0.875rem",
                          fontWeight: 700,
                          color: "#166534",
                        }}
                      >
                        Depreciation Calculation Preview
                      </Typography>
                      <PreviewGrid>
                        {[
                          { label: "Method", value: depreciationPreview.label },
                          {
                            label: "Formula",
                            value: depreciationPreview.formula,
                            mono: true,
                          },
                          {
                            label: "Annual Depreciation",
                            value: `${formatCurrency(depreciationPreview.annualDepreciation)}/year`,
                            highlight: true,
                          },
                          {
                            label: "Total Depreciable Amount",
                            value: formatCurrency(
                              depreciationPreview.depreciableAmount,
                            ),
                          },
                          ...(depreciationPreview.yearsElapsed > 0
                            ? [
                                {
                                  label: "Years Elapsed (as of Today)",
                                  value: `${depreciationPreview.yearsElapsed} yrs`,
                                },
                              ]
                            : []),
                          {
                            label: "Current Book Value (Today)",
                            value: formatCurrency(
                              depreciationPreview.currentBookValue,
                            ),
                            green: true,
                          },
                          {
                            label: "Accumulated Depreciation (To Date)",
                            value: formatCurrency(
                              depreciationPreview.accumulatedDepreciation,
                            ),
                            red: true,
                          },
                          ...(depreciationPreview.method === "SLM"
                            ? [
                                {
                                  label: "Useful Life",
                                  value: `${depreciationPreview.years} Years`,
                                },
                                {
                                  label: "Salvage Value",
                                  value: formatCurrency(
                                    toNumber(formData.residualValue),
                                  ),
                                },
                              ]
                            : [
                                {
                                  label: "Opening Book Value",
                                  value: formatCurrency(
                                    depreciationPreview.openingBookValue,
                                  ),
                                },
                                {
                                  label: "WDV Rate",
                                  value: `${depreciationPreview.rate}% per annum`,
                                },
                                {
                                  label: "Residual Value",
                                  value: "Not applicable (₹0)",
                                  muted: true,
                                },
                              ]),
                        ].map(
                          ({
                            label,
                            value,
                            mono,
                            highlight,
                            green,
                            red,
                            muted,
                          }) => (
                            <Box
                              key={label}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.25rem",
                                ...(green && {
                                  padding: "0.5rem",
                                  background: "#ecfdf5",
                                  borderRadius: "6px",
                                  border: "1px solid #6ee7b7",
                                }),
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.6875rem",
                                  fontWeight: 600,
                                  color: green ? "#065f46" : "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                }}
                              >
                                {label}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize:
                                    highlight || green
                                      ? "0.9375rem"
                                      : "0.875rem",
                                  fontWeight:
                                    highlight || green || red ? 700 : 600,
                                  color:
                                    highlight || green
                                      ? "#059669"
                                      : red
                                        ? "#dc2626"
                                        : muted
                                          ? "#6b7280"
                                          : "#1f2937",
                                  fontFamily: mono ? "monospace" : "inherit",
                                  fontStyle: muted ? "italic" : "normal",
                                  wordBreak: "break-all",
                                }}
                              >
                                {value}
                              </Typography>
                            </Box>
                          ),
                        )}
                      </PreviewGrid>

                      <Box sx={{ borderTop: "1px solid #bbf7d0", pt: "1rem" }}>
                        <Typography
                          sx={{
                            margin: "0 0 0.75rem 0",
                            fontSize: "0.8125rem",
                            fontWeight: 700,
                            color: "#374151",
                          }}
                        >
                          {depreciationPreview.method === "SLM"
                            ? "Straight-Line Value Schedule"
                            : "Written-Down Value Schedule (first 5 years)"}
                        </Typography>
                        {depreciationPreview.method === "WDV" && (
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: "#6b7280",
                              mb: 1,
                              fontStyle: "italic",
                            }}
                          >
                            Showing first 5 years. Full schedule available in
                            asset reports.
                          </Typography>
                        )}
                        <ScheduleGrid>
                          {depreciationPreview.yearValues.map((row) => (
                            <Box
                              key={`${depreciationPreview.method}-${row.year}`}
                              sx={{
                                padding: "0.75rem",
                                background: "#fff",
                                border: "1px solid #e5e7eb",
                                borderRadius: "6px",
                                textAlign: "center",
                              }}
                            >
                              <Typography
                                sx={{
                                  display: "block",
                                  fontSize: "0.6875rem",
                                  fontWeight: 600,
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  mb: "0.25rem",
                                }}
                              >
                                Year {row.year}
                              </Typography>
                              <Typography
                                sx={{
                                  display: "block",
                                  fontSize: "0.9375rem",
                                  fontWeight: 700,
                                  color: "#1f2937",
                                }}
                              >
                                {formatCurrency(row.closing)}
                              </Typography>
                              <Typography
                                sx={{
                                  display: "block",
                                  fontSize: "0.6875rem",
                                  color: "#9ca3af",
                                  mt: "0.125rem",
                                }}
                              >
                                Depreciation: {formatCurrency(row.depreciation)}
                              </Typography>
                              {row.accumulated !== undefined && (
                                <Typography
                                  sx={{
                                    display: "block",
                                    fontSize: "11px",
                                    color: "#6b7280",
                                    mt: "0.125rem",
                                  }}
                                >
                                  Accumulated: {formatCurrency(row.accumulated)}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </ScheduleGrid>
                      </Box>
                    </PreviewBox>
                  )}

                  {!depreciationPreview &&
                    formData.depreciationMethod &&
                    formData.purchaseValue && (
                      <InfoBox warning sx={{ mt: "12px" }}>
                        <Typography sx={{ margin: 0, color: "#92400e" }}>
                          ⚠️ Enter missing depreciation parameters (
                          {formData.depreciationMethod ===
                          DEPRECIATION_METHODS.SLM
                            ? "useful life"
                            : "depreciation rate"}
                          ) to see calculated depreciation.
                          {formData.depreciationMethod ===
                          DEPRECIATION_METHODS.WDV
                            ? " Residual Value is automatically set to ₹0 for WDV."
                            : " Residual Value defaults to ₹0 if left blank."}
                        </Typography>
                      </InfoBox>
                    )}
                </CardContent>
              </SectionCard>
            )}

            {/* SECTION 5 — LOCATION & MAINTENANCE */}
            <SectionCard variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <SectionTitle>Location &amp; Maintenance</SectionTitle>

                {inheritedFromParent &&
                  hasLocationInheritance(formData.assetType) && (
                    <InfoBox success sx={{ mb: 2 }}>
                      <Typography sx={{ margin: 0, fontSize: "inherit" }}>
                        ✓ Location pre-filled from{" "}
                        <strong>{inheritedFromParent.assetName}</strong>. You
                        may override any field below if required.
                      </Typography>
                    </InfoBox>
                  )}

                <FormRow>
                  <FormGroup hasError={Boolean(errors.locationCode)}>
                    <FieldLabel>
                      Target Location Base <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <SearchableDropdown
                      id="locationCode"
                      name="locationCode"
                      value={formData.locationCode || ""}
                      onChange={handleSearchableChange}
                      options={locations}
                      getOptionLabel={(loc) =>
                        loc ? `${loc.locationName} (${loc.locationCode})` : ""
                      }
                      getOptionValue={(loc) => loc?.locationCode || ""}
                      placeholder="Search location..."
                      label=""
                      required={false}
                      disabled={isEditMode && isLocationLocked}
                      error={Boolean(errors.locationCode)}
                      helperText={errors.locationCode || ""}
                      fullWidth={true}
                      sx={{ width: "100%" }}
                      filterOptions={(loc, searchTerm) => {
                        if (!loc) return false;
                        const search = searchTerm.toLowerCase().trim();
                        const name = (loc.locationName || "").toLowerCase();
                        const code = (loc.locationCode || "").toLowerCase();
                        const city = (loc.city || "").toLowerCase();
                        const state = (loc.state || "").toLowerCase();
                        return (
                          name.includes(search) ||
                          code.includes(search) ||
                          city.includes(search) ||
                          state.includes(search)
                        );
                      }}
                      noOptionsText="No locations found"
                    />
                    {!isEditMode && (
                      <FieldHint sx={{ color: "#92400e", fontStyle: "normal" }}>
                        ⚠️ Choose carefully — location cannot be changed after
                        creation. Use <strong>Move Assets</strong> later if
                        needed.
                      </FieldHint>
                    )}

                    {isEditMode && formData.currentLocationName && (
                      <FieldHint
                        sx={{
                          color: "#374151",
                          fontStyle: "normal",
                          fontWeight: 500,
                        }}
                      >
                        Current location:{" "}
                        <strong>{formData.currentLocationName}</strong>
                      </FieldHint>
                    )}
                  </FormGroup>

                 <FormGroup>
  <FieldLabel>Maintenance Interval</FieldLabel>
  <FormControl fullWidth size="small" disabled={isLoadingOptions}>
    <Select
      id="maintenanceFrequency"
      name="maintenanceFrequency"
      value={formData.maintenanceFrequency || ""}
      onChange={handleChange}
      displayEmpty
      renderValue={(selected) => {
        if (!selected) {
          return isLoadingOptions ? "Loading…" : "None Specified";
        }
        const found = maintenanceIntervalOptions.find(
          (o) => o.value === selected
        );
        return found ? found.label : selected;
      }}
    >
      <MenuItem value="">
        {isLoadingOptions ? "Loading…" : "None Specified"}
      </MenuItem>
      {maintenanceIntervalOptions.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</FormGroup>

                  <FormGroup>
                    <FieldLabel>Maintenance Start Date</FieldLabel>
                    <TextField
                      id="maintenanceStartDate"
                      name="maintenanceStartDate"
                      type="date"
                      value={formData.maintenanceStartDate}
                      onChange={handleChange}
                      fullWidth
                      size="small"
                    />
                  </FormGroup>
                </FormRow>

                {formData.locationCode && (
                  <FormRow>
                    <FormGroup>
                      <FieldLabel>Resolved Address Profile</FieldLabel>
                      <TextField
                        multiline
                        rows={2}
                        disabled
                        value={
                          // Find location from locations array
                          (() => {
                            const loc = locations.find(l => l.locationCode === formData.locationCode);
                            return loc ? `${loc.locationName || ""}, ${loc.city || ""}, ${loc.state || ""}` : "Location details not found";
                          })()
                        }
                        fullWidth
                        size="small"
                      />
                    </FormGroup>
                  </FormRow>
                )}
              </CardContent>
            </SectionCard>

            {/* SECTION 6 — CUSTODY ASSIGNMENT */}
            <SectionCard variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <SectionTitle>Custody Assignment Controls</SectionTitle>

                {inheritedFromParent &&
                  hasLocationInheritance(formData.assetType) && (
                    <InfoBox success sx={{ mb: 2 }}>
                      <Typography sx={{ margin: 0, fontSize: "inherit" }}>
                        ✓ Department &amp; custodian pre-filled from{" "}
                        <strong>{inheritedFromParent.assetName}</strong>. You
                        may override if required.
                      </Typography>
                    </InfoBox>
                  )}

                <FormRow>
                  <FormGroup hasError={Boolean(errors.status)}>
                    <FieldLabel>
                      Asset Operating Status <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <FormControl
                      fullWidth
                      size="small"
                      error={Boolean(errors.status)}
                    >
                                         <Select
                        id="status"
                        name="status"
                        value={formData.status || ""}
                        onChange={handleChange}
                        disabled={isLoadingOptions}
                        renderValue={(selected) => {
                          if (!selected) {
                            return isLoadingOptions
                              ? "Loading…"
                              : "Select Status";
                          }
                          const found = assetStatusOptions.find(
                            (o) => o.value === selected,
                          );
                          return found ? found.label : selected;
                        }}
                      >
                        {assetStatusOptions.length === 0 && (
                          <MenuItem value="">
                            {isLoadingOptions ? "Loading…" : "Select Status"}
                          </MenuItem>
                        )}
                        {assetStatusOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.status && (
                        <ErrorMessage>{errors.status}</ErrorMessage>
                      )}
                    </FormControl>
                    {isEditMode && isAssignmentLocked && (
                      <FieldHint
                        sx={{
                          color: "#0369a1",
                          fontStyle: "normal",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <LockIcon sx={{ fontSize: "0.7rem" }} />
                        Managed via Assign Asset transaction
                      </FieldHint>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <FieldLabel>Assigned Department</FieldLabel>
                    <FormControl fullWidth size="small" disabled={isEditMode && isAssignmentLocked}>
                      <Select
                        id="assignedDepartmentId"
                        name="assignedDepartmentId"
                        value={formData.assignedDepartmentId || ""}
                        onChange={handleChange}
                        displayEmpty
                      >
                        <MenuItem value="">None Selected</MenuItem>
                        {departments.map((dept) => (
                          <MenuItem key={dept.id} value={dept.id}>
                            {dept.name || dept.departmentName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {isEditMode && isAssignmentLocked && (
                      <FieldHint
                        sx={{
                          color: "#0369a1",
                          fontStyle: "normal",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <LockIcon sx={{ fontSize: "0.7rem" }} />
                        Managed via Assign Asset transaction
                      </FieldHint>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <FieldLabel>Assigned To (User Custodian)</FieldLabel>
                    <SearchableDropdown
                      id="assignedTo"
                      name="assignedTo"
                      value={formData.assignedTo || ""}
                      onChange={handleSearchableChange}
                      options={users}
                      getOptionLabel={(user) =>
                        user
                          ? `${user.name || user.displayName} — ${user.departmentName || "General Staff"}`
                          : ""
                      }
                      getOptionValue={(user) => user?.id || ""}
                      placeholder="Search user..."
                      label=""
                      required={false}
                      disabled={isEditMode && isAssignmentLocked}
                      error={Boolean(errors.assignedTo)}
                      helperText={errors.assignedTo || ""}
                      fullWidth={true}
                      sx={{ width: "100%" }}
                      filterOptions={(user, searchTerm) => {
                        if (!user) return false;
                        const search = searchTerm.toLowerCase().trim();
                        const name = (user.name || user.displayName || "")
                          .toLowerCase();
                        const dept = (user.departmentName || "").toLowerCase();
                        const email = (user.email || "").toLowerCase();
                        return (
                          name.includes(search) ||
                          dept.includes(search) ||
                          email.includes(search)
                        );
                      }}
                      noOptionsText="No users found"
                    />
                    {isEditMode && isAssignmentLocked && (
                      <FieldHint
                        sx={{
                          color: "#0369a1",
                          fontStyle: "normal",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <LockIcon sx={{ fontSize: "0.7rem" }} />
                        Managed via Assign Asset transaction
                      </FieldHint>
                    )}
                  </FormGroup>
                </FormRow>
              

               
                {/* ── ASSET OWNER (only when ownership tracking is enabled) ── */}

                {ownershipEnabled && (
                  <FormRow>
                    <FormGroup hasError={Boolean(errors.ownerId)}>
                      <FieldLabel>
                        Asset Owner
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "0.7rem",
                            color: "#6b7280",
                            fontWeight: 400,
                            ml: "0.25rem",
                            textTransform: "none",
                          }}
                        >
                          (from users list)
                        </Typography>
                      </FieldLabel>
                      <SearchableDropdown
                        id="ownerId"
                        name="ownerId"
                        value={formData.ownerId || ""}
                        onChange={handleSearchableChange}
                        options={users}
                        getOptionLabel={(user) =>
                          user
                            ? `${user.name || user.displayName} — ${
                                user.departmentName || "General Staff"
                              }`
                            : ""
                        }
                        getOptionValue={(user) => user?.id || ""}
                        placeholder="Search asset owner..."
                        label=""
                        required={false}
                        disabled={ownershipPrefLoading}
                        error={Boolean(errors.ownerId)}
                        helperText={errors.ownerId || ""}
                        fullWidth={true}
                        sx={{ width: "100%" }}
                        filterOptions={(user, searchTerm) => {
                          if (!user) return false;
                          const search = searchTerm.toLowerCase().trim();
                          const name = (
                            user.name ||
                            user.displayName ||
                            ""
                          ).toLowerCase();
                          const dept = (
                            user.departmentName || ""
                          ).toLowerCase();
                          const email = (user.email || "").toLowerCase();
                          return (
                            name.includes(search) ||
                            dept.includes(search) ||
                            email.includes(search)
                          );
                        }}
                        noOptionsText="No users found"
                      />
                      <FieldHint>
                        Owner can edit or modify this asset. Leave blank if no
                        specific owner is required.
                      </FieldHint>
                    </FormGroup>
                  </FormRow>
              
                )}
                                                              {approverEnabled &&
                  (!isEditMode ||
                    (Array.isArray(asset?.approverList) &&
                      asset.approverList.length > 0)) && (
                  <>
                    {approverLevels.length === 0 ? (
                      <InfoBox warning sx={{ mb: 0 }}>
                        <Typography sx={{ margin: 0, fontSize: "inherit" }}>
                          Approval workflow is enabled, but no approvers are
                          configured. Set up the Approver catalog under <strong>Settings → Dropdown Catalogs</strong>.
                        </Typography>
                      </InfoBox>
                    ) : (
                      <>
                        <FormRow>
                          {approverLevels.map((lvl) => {
                            const entry = approverOptionsByLevel.get(lvl);
                            const fieldName = `approverLevel_${lvl}`;
                           const currentRow =
  (formData.approverList || []).find(
    (r) => Number(r.level) === lvl,
  ) || null;
const currentValue = currentRow?.email || "";
                            const errKey = `approverLevel_${lvl}`;
                            const labelText =
                              entry?.label || `Level ${lvl}`;
                            // Show "(Level N)" suffix when the SVL label
                            // doesn't already contain the word "level"
                            const suffix = /level/i.test(labelText)
                              ? ""
                              : ` (Level ${lvl})`;

                          const assetRow =
  (Array.isArray(asset?.approverList) ? asset.approverList : []).find(
    (r) => Number(r.level) === lvl,
  ) || null;
const thisLevelApproved = String(assetRow?.status || "")
  .toLowerCase()
  .startsWith("approved");
const isLocked = isApproverLevelLocked(lvl);

const displayValue = currentRow?.name
  ? `${currentRow.name} (${currentRow.email})`
  : currentValue || "—";

return (
  <FormGroup key={lvl} hasError={Boolean(errors[errKey])}>
    <FieldLabel>
      {labelText}
      {suffix && (
        <Typography
          component="span"
          sx={{
            fontSize: "0.7rem",
            color: "#6b7280",
            fontWeight: 400,
            ml: "0.25rem",
            textTransform: "none",
          }}
        >
          {suffix}
        </Typography>
      )}{" "}
      {!isLocked && <RequiredMark>*</RequiredMark>}
    </FieldLabel>

    {isLocked ? (
      <>
        <TextField
          value={displayValue}
          disabled
          fullWidth
          size="small"
          sx={{
            "& .MuiInputBase-input.Mui-disabled": {
              backgroundColor: "#f3f4f6",
              color: "#374151",
              WebkitTextFillColor: "#374151",
              cursor: "not-allowed",
            },
          }}
          InputProps={{
            endAdornment: thisLevelApproved ? (
              <Chip
                label="Approved"
                color="success"
                size="small"
                sx={{ mr: 1, fontWeight: 600 }}
              />
            ) : (
              <LockIcon sx={{ fontSize: "0.9rem", color: "#6b7280", mr: 1 }} />
            ),
          }}
        />
        <FieldHint sx={{ color: "#0369a1", fontStyle: "normal" }}>
          {approverLevels.length === 1
            ? "Single-approver workflow — fixed at creation."
            : thisLevelApproved
              ? "This level has already approved."
              : "Locked — approval workflow is in progress."}
        </FieldHint>
      </>
    ) : (
      <>
              <Autocomplete
          multiple
          id={fieldName}
          options={entry?.users || []}
          getOptionLabel={(option) => 
            typeof option === "string" ? option : `${option.label} (${option.value})`
          }
          isOptionEqualToValue={(option, val) => option.value === val.value}
          value={(entry?.users || []).filter((u) => (currentRow?.users || []).includes(u.value))}
          onChange={(event, newValue) => {
            // Extract selected emails and pass them to the handler
            const emails = newValue.map((u) => u.value);
            handleSearchableChange({
              target: { name: fieldName, value: emails },
            });
          }}
          disabled={approverPrefLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={currentRow?.users?.length ? "" : `Select ${labelText}...`}
              error={Boolean(errors[errKey])}
              helperText={errors[errKey] || ""}
              FormHelperTextProps={{ sx: helperErrorSx }}
            />
          )}
          renderTags={(selected, getTagProps) =>
            selected.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={option.label}
                size="small"
                sx={{ borderRadius: 1 }}
              />
            ))
          }
        />
                             <FieldHint>
                  {entry?.users.length === 0
                    ? "No users assigned to this level."
                    : entry?.users.length === 1
                      ? `${entry.users[0].label} is the only eligible user. They will be auto-selected.`
                      : `Select 1 or more specific people from the ${entry.users.length} eligible users.`}
                </FieldHint>
      </>
    )}
  </FormGroup>
);
                          })}
                        </FormRow>

                     
                      </>
                    )}
                  </>
                )}
              </CardContent>             
            </SectionCard>
          </Stack>
        </Container>
      </Box>
      {/* STICKY FOOTER */}
      <Box
        sx={{
          flexShrink: 0,
          width: "100%",
          height: "72px",
          maxHeight: "72px",
          boxSizing: "border-box",
          borderTop: "1px solid #e2e8f0",
          backgroundColor: "#fff",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.04)",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1.5,
          px: 3,
        }}
      >
        <Button
          type="button"
          onClick={onClose}
          disabled={loading}
          sx={cancelBtnSx}
        >
          Cancel
        </Button>
              <Button
          type="submit"
          form="asset-form"

          disabled={loading || isSubmissionBlockedByApproval}
          sx={submitBtnSx}
          title={
            isSubmissionBlockedByApproval
              ? `Cannot save asset in ${assetApprovalLabel} status`
              : ""
          }
        >
          {loading && <CircularProgress size={16} color="inherit" />}
          {loading
            ? "Processing…"
            : isSubmissionBlockedByApproval
              ? "Submission Locked"
              : "Submit"}
        </Button>
      </Box>

      {/* ERROR SNACKBAR */}
      <Snackbar
        open={Boolean(submitError)}
        autoHideDuration={6000}
        onClose={() => setSubmitError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={() => setSubmitError("")}
          severity="error"
          variant="filled"
          icon={<ErrorOutlineIcon />}
          sx={{
            width: "100%",
            minWidth: 400,
            maxWidth: 600,
            fontSize: "0.9rem",
            fontWeight: 500,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            alignItems: "center",
          }}
        >
          {submitError}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default CreateEditAssetModal;